import "@/App.css";
import "@/styles/snippets.css";
import "@/i18n";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { RaroBrandProvider } from "@/pages/raro/RaroBrandContext";
import { Toaster } from "@/components/ui/sonner";
import useSEO from "@/hooks/useSEO";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { VoiceActionsProvider } from "@/context/VoiceActionsContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { DietaryProvider } from "@/context/DietaryContext";
import { DietaryProfileProvider } from "@/context/DietaryProfileContext";
import LanguageDetectedToast from "@/components/LanguageDetectedToast";
import { BusinessProvider } from "@/context/BusinessContext";
import ThemeInjector from "@/components/ThemeInjector";
import HomePage from "@/pages/HomePage";
import MenuPage from "@/pages/MenuPage";
import BookingPage from "@/pages/BookingPage";
import CoffeeOrderPage from "@/pages/CoffeeOrderPage";
import RecipeBookPage from "@/pages/RecipeBookPage";
import RecipeDetailPage from "@/pages/RecipeDetailPage";
import LoginPage from "@/pages/LoginPage";
import MagicLinkVerifyPage from "@/pages/MagicLinkVerifyPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DocsPage from "@/pages/DocsPage";
import DemoModeBanner from "@/components/DemoModeBanner";
// Standalone sales pages for module bare-domains — lazy so they only
// load when a visitor lands on the corresponding hostname (or the
// preview route). Keeps the main SPA bundle lean.
const HustleEatsSalesPage    = lazy(() => import("@/pages/HustleEatsSalesPage"));
const HustleConnectSalesPage = lazy(() => import("@/pages/HustleConnectSalesPage"));
const HustleReviewsSalesPage = lazy(() => import("@/pages/HustleReviewsSalesPage"));
const StadiumOpsConsolePage  = lazy(() => import("@/pages/stadium/StadiumOpsConsolePage"));
const SeasonLockSuccessPage  = lazy(() => import("@/pages/stadium/SeasonLockSuccessPage"));
// ── Admin surfaces: lazy-loaded (only logged-in admins ever hit these) ─
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminMenu = lazy(() => import("@/pages/admin/AdminMenu"));
const MenuBuilderV3Page = lazy(() => import("@/pages/menuv3/MenuBuilderV3Page"));
const NewsletterOpsPage = lazy(() => import("@/pages/admin/NewsletterOpsPage"));
const AdminMyProfilePage = lazy(() => import("@/pages/admin/AdminMyProfilePage"));
const VerifyPage = lazy(() => import("@/pages/VerifyPage"));
const VerifyPhotoTokenPage = lazy(() => import("@/pages/VerifyPhotoTokenPage"));
const AdminInventoryPage = lazy(() => import("@/pages/admin/AdminInventoryPage"));
const AdminBookings = lazy(() => import("@/pages/admin/AdminBookings"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminFloorplan = lazy(() => import("@/pages/admin/AdminFloorplan"));
const AdminAvailability = lazy(() => import("@/pages/admin/AdminAvailability"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminTestimonialsPage = lazy(() => import("@/pages/admin/AdminTestimonialsPage"));
const HustlePressPage = lazy(() => import("@/pages/HustlePressPage"));
const HustleNewsSalesPage = lazy(() => import("@/pages/HustleNewsSalesPage"));
const FriendlyErrorPage = lazy(() => import("@/pages/FriendlyErrorPage"));
const AdminPages = lazy(() => import("@/pages/admin/AdminPages"));
const AdminMenuImport = lazy(() => import("@/pages/admin/AdminMenuImport"));
const AdminMenuImportVision = lazy(() => import("@/pages/admin/AdminMenuImportVision"));
const AdminMapGeofence = lazy(() => import("@/pages/admin/AdminMapGeofence"));
const AdminHolidays = lazy(() => import("@/pages/admin/AdminHolidays"));
const AdminComingSoon = lazy(() => import("@/pages/admin/AdminComingSoon"));
const AdminPrintGallery = lazy(() => import("@/pages/admin/AdminPrintGallery"));
const AdminVouchers = lazy(() => import("@/pages/admin/AdminVouchers"));
const NotificationPrefsPage = lazy(() => import("@/pages/admin/NotificationPrefsPage"));
const DeliveryProvidersPage = lazy(() => import("@/pages/admin/DeliveryProvidersPage"));
const SquareIntegrationPage = lazy(() => import("@/pages/admin/SquareIntegrationPage"));
const SmartMenuImportPage = lazy(() => import("@/pages/admin/SmartMenuImportPage"));
const IntegrationsHubPage = lazy(() => import("@/pages/admin/IntegrationsHubPage"));
const AdminLanguages = lazy(() => import("@/pages/admin/AdminLanguages"));
const AdminDbHealthPage = lazy(() => import("@/pages/admin/AdminDbHealthPage"));
const AdminDirectoryRolloutPage = lazy(() => import("@/pages/admin/AdminDirectoryRolloutPage"));
const AdminAlertsPage = lazy(() => import("@/pages/admin/AdminAlertsPage"));
const AdminVpnBlocksPage = lazy(() => import("@/pages/admin/AdminVpnBlocksPage"));
const AdminPropertyOftPage = lazy(() => import("@/pages/admin/AdminPropertyOftPage"));
const AdminKitchenOverheadsPage = lazy(() => import("@/pages/admin/AdminKitchenOverheadsPage"));
const AdminKitchenUsagePage = lazy(() => import("@/pages/admin/AdminKitchenUsagePage"));
const AdminKitchenReorderPage = lazy(() => import("@/pages/admin/AdminKitchenReorderPage"));
const AccountNewsletterPage = lazy(() => import("@/pages/account/AccountNewsletterPage"));
const AccountHustleConnectPage = lazy(() => import("@/pages/account/AccountHustleConnectPage"));
const FleetStatusPage = lazy(() => import("@/pages/FleetStatusPage"));
const AccountQRCodesPage = lazy(() => import("@/pages/account/AccountQRCodesPage"));
const AccountVendorSettingsPage = lazy(() => import("@/pages/account/AccountVendorSettingsPage"));
const AccountWalletCertsPage = lazy(() => import("@/pages/account/AccountWalletCertsPage"));
const AdminDirectoryPage = lazy(() => import("@/pages/admin/AdminDirectoryPage"));
const AdminTemplatePickerPage = lazy(() => import("@/pages/admin/AdminTemplatePickerPage"));
const QRPublicPage = lazy(() => import("@/pages/qr/QRPublicPage"));
const TapRouterPageLazy = lazy(() => import("@/pages/tapnfc/TapRouterPage"));
const RaroLandingPage  = lazy(() => import("@/pages/raro/RaroLandingPage"));
const RaroMenuPage     = lazy(() => import("@/pages/raro/RaroMenuPage"));
const RaroTablePage    = lazy(() => import("@/pages/raro/RaroTablePage"));
const RaroTakeawayPage = lazy(() => import("@/pages/raro/RaroTakeawayPage"));
const RaroPosPage      = lazy(() => import("@/pages/raro/RaroPosPage"));
const RaroAdminPage    = lazy(() => import("@/pages/raro/RaroAdminPage"));
const RaroSiteRouter   = lazy(() => import("@/pages/raro/RaroSiteRouter"));
const RaroStaffLoginPage    = lazy(() => import("@/pages/raro/RaroStaffLoginPage"));
const RaroStaffTerminalPage = lazy(() => import("@/pages/raro/RaroStaffTerminalPage"));
const RaroStaffKitchenPage  = lazy(() => import("@/pages/raro/RaroStaffKitchenPage"));
const RaroStaffSettingsPage = lazy(() => import("@/pages/raro/RaroStaffSettingsPage"));
const RaroStaffAuditPage    = lazy(() => import("@/pages/raro/RaroStaffAuditPage"));
const RaroStaffQRPage       = lazy(() => import("@/pages/raro/RaroStaffQRPage"));
const RaroStaffClosePage    = lazy(() => import("@/pages/raro/RaroStaffClosePage"));
const RaroOwnerBar          = lazy(() => import("@/pages/raro/RaroOwnerBar"));
const RaroBookingPageLazy   = lazy(() => import("@/pages/raro/RaroBookingPage"));
const EventInquiryPage = lazy(() => import("@/pages/events/EventInquiryPage"));
const SuburbPreviewPage = lazy(() => import("@/pages/newsletter/SuburbPreviewPage"));
const AdminConversionsPage = lazy(() => import("@/pages/admin/AdminConversionsPage"));
const PickupOrderPage = lazy(() => import("@/pages/pickup/PickupOrderPage"));
// HustleShield Forms — drag-drop form builder + public form view
const FormsList         = lazy(() => import("@/pages/forms/FormsList"));
const FormsDashboard    = lazy(() => import("@/pages/forms/FormsDashboard"));
const FormBuilder       = lazy(() => import("@/pages/forms/FormBuilderV2"));
const FormSubmissions   = lazy(() => import("@/pages/forms/FormSubmissions"));
const PublicFormView    = lazy(() => import("@/pages/forms/PublicFormView"));
const ShieldDashboard   = lazy(() => import("@/pages/forms/ShieldDashboard"));
const AgencyConsole     = lazy(() => import("@/pages/forms/AgencyConsole"));
const HustleShieldSalesAUD = lazy(() => import("@/pages/forms/HustleShieldSalesAUD"));
const HustleShieldSalesUSD = lazy(() => import("@/pages/forms/HustleShieldSalesUSD"));
const VendorScannerPage = lazy(() => import("@/pages/vendor/VendorScannerPage"));
const BrisbaneLivePulsePage = lazy(() => import("@/pages/live/BrisbaneLivePulsePage"));
const BackupDemoScanPage = lazy(() => import("@/pages/wp/BackupDemoScanPage"));
const RecipePlannerPage = lazy(() => import("@/pages/recipes/RecipePlannerPage"));
const HustleStaysPage = lazy(() => import("@/pages/stays/HustleStaysPage"));
const StaysWelcomeBookPage = lazy(() => import("@/pages/stays/StaysWelcomeBookPage"));
const StaysWelcomeBookPublicPage = lazy(() => import("@/pages/stays/StaysWelcomeBookPublicPage"));
const StaysHostDashboardPage = lazy(() => import("@/pages/stays/StaysHostDashboardPage"));
const StaysComplianceWizardPage = lazy(() => import("@/pages/stays/StaysComplianceWizardPage"));
const StaysFindMePage = lazy(() => import("@/pages/stays/StaysFindMePage"));
const StaysListPropertyPage = lazy(() => import("@/pages/stays/StaysListPropertyPage"));
const StayBookPage = lazy(() => import("@/pages/stays/StayBookPage"));
const AdminStayCoverageBadgePage = lazy(() => import("@/pages/admin/AdminStayCoverageBadgePage"));
const AdminHotelbedsMapPage = lazy(() => import("@/pages/admin/AdminHotelbedsMapPage"));
const HustleTicketsLandingPage = lazy(() => import("@/pages/tickets/HustleTicketsLandingPage"));
const HustleTicketsFeaturesPage = lazy(() => import("@/pages/tickets/HustleTicketsFeaturesPage"));
const HustleTicketsPricingPage = lazy(() => import("@/pages/tickets/HustleTicketsPricingPage"));
const HustleTicketsSolutionsPage = lazy(() => import("@/pages/tickets/HustleTicketsSolutionsPage"));
const HustleTicketsFaqPage = lazy(() => import("@/pages/tickets/HustleTicketsFaqPage"));
const HustleTicketsContactPage = lazy(() => import("@/pages/tickets/HustleTicketsContactPage"));
const TicketsBrowsePage = lazy(() => import("@/pages/tickets/TicketsBrowsePage"));
const SeatingMakerLandingPage = lazy(() => import("@/pages/SeatingMakerLandingPage"));
const SeatingMakerEditorPage = lazy(() => import("@/pages/SeatingMakerEditorPage"));
const SeatingMakerMapEditPage = lazy(() => import("@/pages/SeatingMakerMapEditPage"));
const AdminSeatingMakerMembersPage = lazy(() => import("@/pages/admin/AdminSeatingMakerMembersPage"));
const TicketsEventDetailPage = lazy(() => import("@/pages/tickets/TicketsEventDetailPage"));
const TicketsPromoterWizardPage = lazy(() => import("@/pages/tickets/TicketsPromoterWizardPage"));
const HustleEventsSeatPickerPage = lazy(() => import("@/pages/tickets/HustleEventsSeatPickerPage"));
const AdminHustleEventsSeatMapPage = lazy(() => import("@/pages/admin/AdminHustleEventsSeatMapPage"));
const AdminVenueMakerPage = lazy(() => import("@/pages/admin/AdminVenueMakerPage"));
const AdminMarketingSegmentsPage = lazy(() => import("@/pages/admin/AdminMarketingSegmentsPage"));
const MyPurchasesPage = lazy(() => import("@/pages/account/MyPurchasesPage"));
const AdminTicketsSettingsPage = lazy(() => import("@/pages/admin/AdminTicketsSettingsPage"));
const HustleScaleSalesPage = lazy(() => import("@/pages/scale/HustleScaleSalesPage"));
const MyCookbookPage = lazy(() => import("@/pages/recipes/MyCookbookPage"));
const PlannerRecipeDetailPage = lazy(() => import("@/pages/recipes/RecipeDetailPage"));
const AdminMichelinPage = lazy(() => import("@/pages/admin/AdminMichelinPage"));
const AdminMarketingStudioPage = lazy(() => import("@/pages/admin/AdminMarketingStudioPage"));
const AgentTeamMindMap = lazy(() => import("@/pages/admin/AgentTeamMindMap"));
const AgentTeamDashboard = lazy(() => import("@/pages/admin/AgentTeamDashboard"));
const SearchFeaturesEditor = lazy(() => import("@/pages/admin/SearchFeaturesEditor"));
const MultilingualCheckPage = lazy(() => import("@/pages/admin/MultilingualCheckPage"));
const MultilingualAuditLandingPage = lazy(() => import("@/pages/MultilingualAuditLandingPage"));
const SiteClonerPage = lazy(() => import("@/pages/admin/SiteClonerPage"));
const LeadsMultilingualPage = lazy(() => import("@/pages/admin/LeadsMultilingualPage"));
const CustomDomainsPage = lazy(() => import("@/pages/admin/CustomDomainsPage"));
const OnboardingWebsiteWizard = lazy(() => import("@/pages/OnboardingWebsiteWizard"));
const AfterClonePolishWizard = lazy(() => import("@/pages/AfterClonePolishWizard"));
const SnapshotsTimelinePage = lazy(() => import("@/pages/admin/SnapshotsTimelinePage"));
// Iter 185 — Trust Ops Approval Engine
const AdminTrustApprovalQueuePage = lazy(() => import("@/pages/admin/AdminTrustApprovalQueuePage"));
const AdminTrustBeneficiariesPage = lazy(() => import("@/pages/admin/AdminTrustBeneficiariesPage"));
const AdminTrustAuditPage = lazy(() => import("@/pages/admin/AdminTrustAuditPage"));
const AdminNotificationChannelsPage = lazy(() => import("@/pages/admin/AdminNotificationChannelsPage"));
// Iter 186/187 — Rentals + Cyber-Ops + Sweep dashboard
const AdminTrustSweepDashboardPage = lazy(() => import("@/pages/admin/AdminTrustSweepDashboardPage"));
const AdminRentalsPage = lazy(() => import("@/pages/admin/AdminRentalsPage"));
// Iter 192 — Dropshipzone Out-Of-Stock queue for support
const AdminDszOosQueuePage = lazy(() => import("@/pages/admin/AdminDszOosQueuePage"));
const AccountVenueEventsPage = lazy(() => import("@/pages/account/AccountVenueEventsPage"));
const DemoPersonasPage = lazy(() => import("@/pages/DemoPersonasPage"));
const AdminEatsEventsPage = lazy(() => import("@/pages/admin/AdminEatsEventsPage"));
const AdminEventPromoPage = lazy(() => import("@/pages/admin/AdminEventPromoPage"));
const EatsEventsCalendarPage = lazy(() => import("@/pages/EatsEventsCalendarPage"));
const EatsEventDetailPage = lazy(() => import("@/pages/EatsEventDetailPage"));
const EatsVenuePage = lazy(() => import("@/pages/EatsVenuePage"));
const HustlePressStartPage = lazy(() => import("@/pages/HustlePressStartPage"));
const HustlePressSuccessPage = lazy(() => import("@/pages/HustlePressSuccessPage"));
// Family Groups V1 — household/share hub + token-gated invite accept
const FamilyGroupsPage = lazy(() => import("@/pages/family/FamilyGroupsPage"));
const FamilyInviteAcceptPage = lazy(() => import("@/pages/family/FamilyInviteAcceptPage"));
import OrderSuccessPage from "@/pages/OrderSuccessPage";
import DszOrderDecidePage from "@/pages/DszOrderDecidePage";
import GroupDiningCreatePage from "@/pages/GroupDiningCreatePage";
import GroupDiningPage from "@/pages/GroupDiningPage";
import GroupMenuPickerPage from "@/pages/GroupMenuPickerPage";
const AdminGroupDiningPage = lazy(() => import("@/pages/admin/AdminGroupDiningPage"));
import OrderCancelPage from "@/pages/OrderCancelPage";
import RegisterPage from "@/pages/RegisterPage";
import AccountPage from "@/pages/AccountPage";
import AgencyControlPanel from "@/pages/AgencyControlPanel";
import HCSettingsPage from "@/pages/HCSettingsPage";
import HustlePlateSalesPage from "@/pages/HustlePlateSalesPage";
// ── HustlePlate sub-sales pages (lazy — big content pages) ─────────────
const HustlePlateRecipeBuilderPage  = lazy(() => import("@/pages/hustleplate/RecipeBuilderPage"));
const HustlePlateTerminalPage       = lazy(() => import("@/pages/hustleplate/TerminalPage"));
const HustlePlateBookingsPage       = lazy(() => import("@/pages/hustleplate/BookingsPage"));
const HustlePlateTableBuilderPage   = lazy(() => import("@/pages/hustleplate/TableBuilderPage"));
const HustlePlateKitchenDisplayPage = lazy(() => import("@/pages/hustleplate/KitchenDisplayPage"));
const HustlePlateMenuBuilderPage    = lazy(() => import("@/pages/hustleplate/MenuBuilderPage"));
const HustlePlateInventoryPage      = lazy(() => import("@/pages/hustleplate/InventoryPage"));
const HustlePlateRewardsPage        = lazy(() => import("@/pages/hustleplate/RewardsPage"));
const HustlePlateAiReceptionistPage = lazy(() => import("@/pages/hustleplate/AiReceptionistPage"));
// ── HustleAnswer (the Hustle Ai Receptionist, rebranded from AI Receptionist) ──
const HustleAnswerSalesPage = lazy(() => import("@/pages/HustleAnswerSalesPage"));
const HustleAnswerDemoPage  = lazy(() => import("@/pages/hustleanswer/HustleAnswerDemoPage"));
const HustleAnswerAppPage   = lazy(() => import("@/pages/hustleanswer/HustleAnswerAppPage"));
// ── HustleQR marketing + qrpal.au apex placeholder ──
const HustleQRSalesPage     = lazy(() => import("@/pages/HustleQRSalesPage"));
const HustleVideosSalesPage = lazy(() => import("@/pages/HustleVideosSalesPage"));
const QRPalPlaceholderPage  = lazy(() => import("@/pages/QRPalPlaceholderPage"));
const AgencyAdminDashboard  = lazy(() => import("@/pages/AgencyAdminDashboard"));
import AbnRegistryDemoPage from "@/pages/AbnRegistryDemoPage";
import AbnRegistryAdminPage from "@/pages/AbnRegistryAdminPage";
import DemoTourPage from "@/pages/DemoTourPage";
import HustleEatsPage from "@/pages/HustleEatsPage";
import HustleEatsWalletPage from "@/pages/HustleEatsWalletPage";
import HustleEatsAdminPage from "@/pages/HustleEatsAdminPage";
import TableSessionPage from "@/pages/TableSessionPage";
import DropshipzonePage from "@/pages/DropshipzonePage";
import SignupHubPage from "@/pages/SignupHubPage";
import SignupPage from "@/pages/SignupPage";
import DirectoryPage from "@/pages/DirectoryPage";
import RestaurantDetailPage from "@/pages/RestaurantDetailPage";
import SiteRouter from "@/pages/SiteRouter";
import OlympicQuickTour from "@/pages/OlympicQuickTour";
import PracticeAdminPanel from "@/pages/PracticeAdminPanel";
import ClaimOnboardPage from "@/pages/ClaimOnboardPage";
import DirectoryAUPage from "@/pages/DirectoryAUPage";
import "@/pages/DirectoryAUPage.css";
import CoffeeCommunePage from "@/pages/CoffeeCommunePage";
// ── Pitch / marketing one-offs: lazy (rare visits) ───────────────────
const OwnerPitchPage = lazy(() => import("@/pages/OwnerPitchPage"));
const DinerPitchPage = lazy(() => import("@/pages/DinerPitchPage"));
const InvestorPitchPage = lazy(() => import("@/pages/InvestorPitchPage"));
const SalonPitchPage = lazy(() => import("@/pages/SalonPitchPage"));
import HustlePlazaPage from "@/pages/HustlePlazaPage";
import HustlePlazaShopPage from "@/pages/HustlePlazaShopPage";
import TackleShopPage from "@/pages/TackleShopPage";
import AdminReimbursementsPage from "@/pages/AdminReimbursementsPage";
import PosRedeemPage from "@/pages/PosRedeemPage";
import RestaurantAgreementPage from "@/pages/RestaurantAgreementPage";
import VenueSignupPage from "@/pages/VenueSignupPage";
import FishingToursPage from "@/pages/FishingToursPage";
import FishMapPage from "@/pages/FishMapPage";
import FishMapHallOfFamePage from "@/pages/FishMapHallOfFamePage";
import HustleUpPublicGroupPage from "@/pages/HustleUpPublicGroupPage";
import HustleUpPublicEventPage from "@/pages/HustleUpPublicEventPage";
import HustleUpDiscoverPage from "@/pages/HustleUpDiscoverPage";
import HustleVoice from "@/components/voice/HustleVoice";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import HustleVoiceSettingsPage from "@/pages/HustleVoiceSettingsPage";
import HustleEmergencyPage from "@/pages/HustleEmergencyPage";
import HustleShopPage from "@/pages/HustleShopPage";
import AdminWakePhrasesPage from "@/pages/admin/AdminWakePhrasesPage";
import AdminVideoStudioPage from "@/pages/admin/AdminVideoStudioPage";
import AdminCreateVideoPage from "@/pages/admin/AdminCreateVideoPage";
import AdminQuickAddPage from "@/pages/admin/AdminQuickAddPage";
import GlobalDeepLinkHandler from "@/components/GlobalDeepLinkHandler";
import CrossModuleVoiceActions from "@/components/CrossModuleVoiceActions";
import AccessibilityWidget from "@/components/AccessibilityWidget";
import SellerPackPage from "@/pages/SellerPackPage";
import TrustAdminPage from "@/pages/TrustAdminPage";
import FishingTourDetailPage from "@/pages/FishingTourDetailPage";
import OperatorClaimPage from "@/pages/OperatorClaimPage";
import HustleUpLandingPage from "@/pages/HustleUpLandingPage";
import HustleUpJoinPage from "@/pages/HustleUpJoinPage";
import HustleUpMePage from "@/pages/HustleUpMePage";
import HustleUpOrganisersPage from "@/pages/HustleUpOrganisersPage";
import HustleUpMessagesPage from "@/pages/HustleUpMessagesPage";
import AdminChatReportsPage from "@/pages/AdminChatReportsPage";
import FohTabletPage from "@/pages/FohTabletPage";
import {
  HustleUpGroupsPage,
  HustleUpGroupCreatePage,
  HustleUpGroupDetailPage,
  HustleUpEventPage,
  HustleUpEventCreatePage,
} from "@/pages/HustleUpGroups";
import {
  HustleChatInboxPage,
  HustleChatNewPage,
  HustleChatThreadPage,
} from "@/pages/HustleChat";
import PlazaAdminPricingPage from "@/pages/PlazaAdminPricingPage";
import PlazaDemoPage from "@/pages/PlazaDemoPage";
import HustleGroupTermsPage from "@/pages/HustleGroupTermsPage";
import PlazaSettingsAdminPage from "@/pages/PlazaSettingsAdminPage";
// ── Newsletter sub-module: lazy ──────────────────────────────────────
const NewsletterListPage = lazy(() => import("@/pages/newsletter/NewsletterListPage"));
const NewsletterEditorPage = lazy(() => import("@/pages/newsletter/NewsletterEditorPage"));
const NewsletterHelpPage = lazy(() => import("@/pages/newsletter/NewsletterHelpPage"));
const PublicNewsletterPage = lazy(() => import("@/pages/newsletter/PublicNewsletterPage"));
const NewsletterArchivePage = lazy(() => import("@/pages/newsletter/NewsletterArchivePage"));
// ── HustleProperty (standalone RE platform): lazy ───────────────────
const PropertyHomePage = lazy(() => import("@/pages/property/PropertyHomePage"));
const MatchMeWizard = lazy(() => import("@/pages/property/MatchMeWizard"));
const SellerListingWizard = lazy(() => import("@/pages/property/SellerListingWizard"));
const SellerInfoPage = lazy(() => import("@/pages/property/SellerInfoPage"));
const FloorPlanDesigner = lazy(() => import("@/pages/eats/FloorPlanDesigner"));
const BuilderStudio = lazy(() => import("@/pages/agency/BuilderStudio"));
const AgencyConsolePage = lazy(() => import("@/pages/agency/AgencyConsolePage"));
const AgencyApiKeysPage = lazy(() => import("@/pages/agency/AgencyApiKeysPage"));
const AgencySettingsPage = lazy(() => import("@/pages/agency/AgencySettingsPage"));
const VenueConsolePage = lazy(() => import("@/pages/portal/VenueConsolePage"));
const VerticalGroupsEditorPage = lazy(() => import("@/pages/agency/VerticalGroupsEditorPage"));
const BusinessPortalPage = lazy(() => import("@/pages/portal/BusinessPortalPage"));
const WorkflowsPage = lazy(() => import("@/pages/portal/WorkflowsPage"));
const AgencyEnginePage = lazy(() => import("@/pages/portal/AgencyEnginePage"));
const BusinessPortalSupportPage = lazy(() => import("@/pages/portal/BusinessPortalSupportPage"));
const PortalApiKeysPage = lazy(() => import("@/pages/portal/ApiKeysPage"));
const BusinessPortalSubscriptionPage = lazy(() => import("@/pages/portal/BusinessPortalSubscriptionPage"));
const PropertyListPage = lazy(() => import("@/pages/property/PropertyListPage"));
const PropertyDetailPage = lazy(() => import("@/pages/property/PropertyDetailPage"));
const PropertyFindPage = lazy(() => import("@/pages/property/PropertyFindPage"));
const PropertyServicesPage = lazy(() => import("@/pages/property/PropertyServicesPage"));
const PropertyServiceDetail = lazy(() => import("@/pages/property/PropertyServiceDetail"));
const PropertyAMLPage = lazy(() => import("@/pages/property/PropertyAMLPage"));
const PropertyAMLForDevelopersPage = lazy(() => import("@/pages/property/PropertyAMLForDevelopersPage"));
const AdminAMLDashboard = lazy(() => import("@/pages/admin/AdminAMLDashboard"));
const TenantIntegrationsPage = lazy(() => import("@/pages/admin/TenantIntegrationsPage"));
const AdminHcSitesPage = lazy(() => import("@/pages/admin/AdminHcSitesPage"));
const AdminIndexNowStatsPage = lazy(() => import("@/pages/admin/AdminIndexNowStatsPage"));
const AdminHcFleetPage = lazy(() => import("@/pages/admin/AdminHcFleetPage"));
const AdminExposureGridPage = lazy(() => import("@/pages/admin/AdminExposureGridPage"));
const MeetupHomePage = lazy(() => import("@/pages/meetup/MeetupHomePage"));
const MeetupOnboardPage = lazy(() => import("@/pages/meetup/MeetupOnboardPage"));
const MeetupGroupPage = lazy(() => import("@/pages/meetup/MeetupGroupPage"));
const MeetupEventPage = lazy(() => import("@/pages/meetup/MeetupEventPage"));
const MeetupEventWelcomePackPage = lazy(() => import("@/pages/meetup/MeetupEventWelcomePackPage"));
const MeetupBuddyPayPage = lazy(() => import("@/pages/meetup/BuddyPayPage"));
const HustleChatRoomPage = lazy(() => import("@/pages/HustleChatRoomPage"));
const PropertySuburbPage = lazy(() => import("@/pages/property/PropertySuburbPage"));
const PropertyGeoAdminPage = lazy(() => import("@/pages/property/PropertyGeoAdminPage"));
const PropertyAdminList = lazy(() => import("@/pages/property/PropertyAdmin").then(m => ({ default: m.PropertyAdminList })));
const PropertyAdminEditor = lazy(() => import("@/pages/property/PropertyAdmin").then(m => ({ default: m.PropertyAdminEditor })));
const PropertyAdminPeople = lazy(() => import("@/pages/property/PropertyAdmin").then(m => ({ default: m.PropertyAdminPeople })));
const PropertyTemplatesPage = lazy(() => import("@/pages/property/PropertyTemplatesPage"));
// ── HustleMusic: lazy ────────────────────────────────────────────────
const HustleMusicApplyPage = lazy(() => import("@/pages/hustlemusic/HustleMusicApplyPage"));
const HustleMusicUploadPage = lazy(() => import("@/pages/hustlemusic/HustleMusicUploadPage"));
const HustleMusicListenPage = lazy(() => import("@/pages/hustlemusic/HustleMusicListenPage"));
const HustleMusicAdminPage = lazy(() => import("@/pages/hustlemusic/HustleMusicAdminPage"));
import ReservePage from "@/pages/ReservePage";
import RestaurantAdminPage from "@/pages/RestaurantAdminPage";
import AgencyDinersPage from "@/pages/AgencyDinersPage";
import ShortcodesAdminPage from "@/pages/ShortcodesAdminPage";
import RecipePage from "@/pages/recipe/RecipePage";
import ClaimPage from "@/pages/recipe/ClaimPage";
import DemoGate from "@/components/DemoGate";
import PlazaHostLockdown from "@/components/PlazaHostLockdown";
import AICopyReviewPanel from "@/pages/AICopyReviewPanel";
import AddBusinessPage from "@/pages/AddBusinessPage";
import DirectorySalesPage from "@/pages/DirectorySalesPage";
import HustleStadiumSalesPage from "@/pages/HustleStadiumSalesPage";
import HustleCRMSalesPage from "@/pages/HustleCRMSalesPage";
import HustlePressSalesPage from "@/pages/HustlePressSalesPage";
import TaxonomyQueuePanel from "@/pages/TaxonomyQueuePanel";
import AdminBookingsPage from "@/pages/AdminBookingsPage";
import AdminModulesPage from "@/pages/AdminModulesPage";
import AdminGlobalHeaderPage from "@/pages/AdminGlobalHeaderPage";
import RoadtripPage from "@/pages/RoadtripPage";
import HotelDetailPage from "@/pages/HotelDetailPage";
import RoadtripDrivePage from "@/pages/RoadtripDrivePage";
// HustleMoments — QR-powered UGC (Memogram-equivalent) module
import MomentsLandingPage from "@/pages/MomentsLandingPage";
import MomentsDashboardPage from "@/pages/MomentsDashboardPage";
import MomentsPageDetailPage from "@/pages/MomentsPageDetailPage";
import MomentPublicUploadPage from "@/pages/MomentPublicUploadPage";
import MomentSlideshowPage from "@/pages/MomentSlideshowPage";
import HustleMeetLobbyPage from "@/pages/HustleMeetLobbyPage";
import HustleMeetRoomPage from "@/pages/HustleMeetRoomPage";
import HustleMeetRatePage from "@/pages/HustleMeetRatePage";
import HustlePointsWalletPage from "@/pages/HustlePointsWalletPage";
import HustlePointsTermsPage from "@/pages/HustlePointsTermsPage";
import PointsOpsPage from "@/pages/admin/PointsOpsPage";
import BellaCucinaCheckoutPage from "@/pages/BellaCucinaCheckoutPage";
import GstPage from "@/pages/legal/GstPage";
import AdminIngredientsPage from "@/pages/AdminIngredientsPage";
import AdminTemplatesPage from "@/pages/AdminTemplatesPage";
import ClaimBusinessPage from "@/pages/ClaimBusinessPage";
import AdminLoyaltyPoolPage from "@/pages/AdminLoyaltyPoolPage";
import AgencyBulkImportPage from "@/pages/admin/AgencyBulkImportPage";
import ClaimDashboardPage from "@/pages/admin/ClaimDashboardPage";
import RecipeGiftAdminPage from "@/pages/admin/RecipeGiftAdminPage";
import RecipeGiftLanding from "@/pages/RecipeGiftLanding";
import AdminLoyaltyPage from "@/pages/admin/AdminLoyaltyPage";
import GalleryPage from "@/pages/GalleryPage";
import AdminGalleryPage from "@/pages/admin/AdminGalleryPage";
import AdminPushPage from "@/pages/admin/AdminPushPage";
import AdminMediaLibraryPage from "@/pages/admin/AdminMediaLibraryPage";
import CMSPage from "@/pages/CMSPage";
import ReviewsPage from "@/pages/ReviewsPage";
const ReviewRespondPage = lazy(() => import("@/pages/ReviewRespondPage"));
const SnapEarnPage = lazy(() => import("@/pages/SnapEarnPage"));
const FeesCalculatorPage = lazy(() => import("@/pages/FeesCalculatorPage"));
const LoginHandoffPage = lazy(() => import("@/pages/LoginHandoffPage"));
import QuickMenuPage from "@/pages/QuickMenuPage";
import ChefSpecialsPrintPage from "@/pages/ChefSpecialsPrintPage";
import BlogIndexPage from "@/pages/BlogIndexPage";
// ── Sales portal + new B2B surfaces: lazy ───────────────────────────
const SalesPage = lazy(() => import("@/pages/sales/SalesPage"));
const HustleSuiteAssistantPage = lazy(() => import("@/pages/HustleSuiteAssistantPage"));
const HustleSuiteSalesPage     = lazy(() => import("@/pages/HustleSuiteSalesPage"));
const MembershipTourismPage    = lazy(() => import("@/pages/membership/MembershipTourismPage"));
const SuitePolishPage = lazy(() => import("@/pages/SuitePolishPage"));
const AgencyFleetStatusPage = lazy(() => import("@/pages/agency/AgencyFleetStatusPage"));
const SalesRecipesPortal = lazy(() => import("@/pages/sales/SalesRecipesPortal"));
const SalesRecipeDetail = lazy(() => import("@/pages/sales/SalesRecipeDetail"));
const ShowcasePage = lazy(() => import("@/pages/ShowcasePage"));
const ShowcaseSuccessPage = lazy(() => import("@/pages/ShowcaseSuccessPage"));
const HydroReportPage = lazy(() => import("@/pages/HydroReportPage"));
const HydroComparePage = lazy(() => import("@/pages/HydroComparePage"));
const HydroApiPage = lazy(() => import("@/pages/HydroApiPage"));
const ApiKeysPage = lazy(() => import("@/pages/ApiKeysPage"));
const SavedPropertiesPage = lazy(() => import("@/pages/SavedPropertiesPage"));
import AccountCreditsPage from "@/pages/AccountCreditsPage";
import { PrivacyRoute, TermsRoute } from "@/components/LegalRoute";
import HustleSuitePrivacyPage from "@/pages/legal/HustleSuitePrivacyPage";
import HustleSuiteTermsPage from "@/pages/legal/HustleSuiteTermsPage";
import HustleSuiteDisclaimerPage from "@/pages/legal/HustleSuiteDisclaimerPage";
import LegalPageShell from "@/components/legal/LegalPageShell";
import SupportPage from "@/pages/SupportPage";
import CreditsPage from "@/pages/CreditsPage";
import HustleFinderPage from "@/pages/HustleFinderPage";
import HustleEventsCatalogPage from "@/pages/HustleEventsCatalogPage";
import HustleEventDetailPage from "@/pages/HustleEventDetailPage";
import HustleEventSuccessPage from "@/pages/HustleEventSuccessPage";
import AdminHustleEventsPage from "@/pages/AdminHustleEventsPage";
import MyLedgerPage from "@/pages/MyLedgerPage";
import VendorLedgerPage from "@/pages/VendorLedgerPage";
import VendorSettingsPage from "@/pages/VendorSettingsPage";
import GiftClaimPage from "@/pages/GiftClaimPage";
import VoucherStorefrontPage from "@/pages/VoucherStorefrontPage";
import VendorPublicPage from "@/pages/VendorPublicPage";
import HustleMeetLandingPage from "@/pages/HustleMeetLandingPage";
import HustleMeetCirclesSalesPage from "@/pages/HustleMeetCirclesSalesPage";
import HustleMeetMatchmakerSalesPage from "@/pages/HustleMeetMatchmakerSalesPage";
import HustleMeetupsOverviewPage from "@/pages/HustleMeetupsOverviewPage";
import HustleMeetupsVerifiedMembersPage from "@/pages/HustleMeetupsVerifiedMembersPage";
import HustleRoundupSalesPage from "@/pages/HustleRoundupSalesPage";
import HustleMeetProfilePage from "@/pages/HustleMeetProfilePage";
import HustleMeetCircleDetailPage from "@/pages/HustleMeetCircleDetailPage";
import HustleMeetHostApplyPage from "@/pages/HustleMeetHostApplyPage";
import HustleMeetHostDashboardPage from "@/pages/HustleMeetHostDashboardPage";
import HustleMeetPostEventPage from "@/pages/HustleMeetPostEventPage";
import HustleMeetMatchmakerIntakePage from "@/pages/HustleMeetMatchmakerIntakePage";
import HustleMeetIDCheckPage from "@/pages/HustleMeetIDCheckPage";
import VenueTerminalPage from "@/pages/VenueTerminalPage";
import KitchenDisplayPage from "@/pages/KitchenDisplayPage";
import VenuePinLoginPage from "@/pages/VenuePinLoginPage";
import VenuePartnerApplyPage from "@/pages/VenuePartnerApplyPage";
import VenuePartnerDashboardPage from "@/pages/VenuePartnerDashboardPage";
import AdminHustleMeetSettingsPage from "@/pages/admin/AdminHustleMeetSettingsPage";
import AdminHustleMeetMatchmakerPage from "@/pages/admin/AdminHustleMeetMatchmakerPage";
import AdminHustleMeetHostAppsPage from "@/pages/admin/AdminHustleMeetHostAppsPage";
import AdminVoucherTemplatesPage from "@/pages/AdminVoucherTemplatesPage";
import FinderPoiDetailPage from "@/pages/FinderPoiDetailPage";
import FinderPuzzlesPage from "@/pages/FinderPuzzlesPage";
import FinderKidZonePage from "@/pages/FinderKidZonePage";
import FinderSudokuPage from "@/pages/FinderSudokuPage";
import FinderMazesPage from "@/pages/FinderMazesPage";
import FinderColouringPage from "@/pages/FinderColouringPage";
import FinderCrosswordPage from "@/pages/FinderCrosswordPage";
import FinderJigsawPage from "@/pages/FinderJigsawPage";
import DiscoverPage from "@/pages/DiscoverPage";
import TableOrderPage from "@/pages/TableOrderPage";
import SiteLayout from "@/components/SiteLayout";
import MeetupLayout from "@/components/MeetupLayout";
import RootRouter from "@/components/RootRouter";
import WhitelabelTopBar from "@/components/WhitelabelTopBar";
import { resolveWhitelabel, resolveRaro } from "@/lib/hostRegistry";
import GlobalSuiteBar from "@/components/GlobalSuiteBar";
import GoogleTranslateBar from "@/components/GoogleTranslateBar";
import ConsoleReturnPill from "@/components/ConsoleReturnPill";
import AppHostGate from "@/components/AppHostGate";
const HustleConnectPage = lazy(() => import("@/pages/HustleConnectPage"));

// ── Polish Foodies tenant (migrated from polishfoodies.com) ──────────
const PolishFoodiesLayout       = lazy(() => import("@/pages/sites/polish-foodies/PolishFoodiesLayout"));
const PolishFoodiesHome         = lazy(() => import("@/pages/sites/polish-foodies/PolishFoodiesHome"));
const PolishFoodiesClub         = lazy(() => import("@/pages/sites/polish-foodies/PolishFoodiesClub"));
const PolishFoodiesRecipes      = lazy(() => import("@/pages/sites/polish-foodies/PolishFoodiesRecipes"));
const PolishFoodiesRecipeDetail = lazy(() => import("@/pages/sites/polish-foodies/PolishFoodiesRecipeDetail"));
const PolishFoodiesShop         = lazy(() => import("@/pages/sites/polish-foodies/PolishFoodiesShop"));
const PolishFoodiesProductDetail = lazy(() => import("@/pages/sites/polish-foodies/PolishFoodiesProductDetail"));
const PolishFoodiesCheckout      = lazy(() => import("@/pages/sites/polish-foodies/PolishFoodiesCheckout"));
const PolishFoodiesMembers       = lazy(() => import("@/pages/sites/polish-foodies/PolishFoodiesMembers"));
const PolishFoodiesAbout        = lazy(() => import("@/pages/sites/polish-foodies/PolishFoodiesAbout"));
const PolishFoodiesContact      = lazy(() => import("@/pages/sites/polish-foodies/PolishFoodiesContact"));
const PolishFoodiesAdmin        = lazy(() => import("@/pages/sites/polish-foodies/PolishFoodiesAdmin"));
const PolishFoodiesFulfilment   = lazy(() => import("@/pages/sites/polish-foodies/PolishFoodiesFulfilment"));
const HustleDealsPage = lazy(() => import("@/pages/HustleDealsPage"));
const CampusPage = lazy(() => import("@/pages/CampusPage"));
import ModuleBranding from "@/components/ModuleBranding";
import { RecipesIndexRouter, RecipesDetailRouter } from "@/components/RecipesRouter";
import ResortLandingPage from "@/pages/ResortLandingPage";
const MarketingStudioQAPage = lazy(() =>
  import("@/pages/admin/MarketingStudioQAPage"));
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import MicTestPage from "@/pages/MicTestPage";
import CloudflareSetupWizard from "@/pages/CloudflareSetupWizard";
const PwaLauncherPage = lazy(() => import("@/pages/PwaLauncherPage"));

function LocationPageWrapper() {
  // /locations/<rest>  →  CMS slug "locations/<rest>"
  const location = useLocation();
  const rest = location.pathname.replace(/^\/locations\//, "");
  return <CMSPage slug={`locations/${rest}`} />;
}

/**
 * WhitelabelWildcard (iter373c patch I — 2026-09-22)
 * ─────────────────────────────────────────────────
 * The apex-only <RootRouter /> handles path="/" for whitelabel hosts
 * (e.g. taxassistoxley.au). But when the visitor deep-links or clicks
 * an internal nav link like /pages/tax-savings, /about, /menu, the
 * SPA's outer catch-all wildcard used to Navigate them back to "/",
 * making every internal link snap to the homepage.
 *
 * This wildcard checks the current hostname against hostRegistry's
 * whitelabel entries; when it matches, it mounts SiteRouter with the
 * hardcoded slug so RestaurantSite's inner <Routes> can pick the
 * right sub-page (ServicePage, ContactPage, etc.). Non-whitelabel
 * hosts keep the original Navigate-to-home behaviour.
 */
function WhitelabelWildcard() {
  const host = typeof window !== "undefined"
    ? (window.location.hostname || "").toLowerCase()
    : "";
  const forceSlug = resolveWhitelabel(host);
  if (forceSlug) return <SiteRouter forceSlug={forceSlug} />;
  return <Navigate to="/" replace />;
}

/**
 * WhitelabelHostGate (iter373c patch J — 2026-09-23)
 * ──────────────────────────────────────────────────
 * The old `<Route path="*"/>` wildcard was being intercepted by other
 * top-level routes (`/locations/*`, `/about`, `/menu`, `/book`, etc.),
 * so a vendor's whitelabel domain would render the platform's SiteLayout
 * (with the tenant's demo brand from `settings.restaurant_name`) instead
 * of the vendor's own site.
 *
 * This gate sits ABOVE the entire <Routes> tree. On a whitelabel host
 * (taxassistoxley.au, vcard.taxassistoxley.au, etc.) it short-circuits
 * every path — root AND sub-paths — into the vendor's `<SiteRouter/>`
 * with `forceSlug`, so the URL never leaks into the shared platform
 * SPA. On every other host it renders children (the normal Routes)
 * unchanged.
 */
function WhitelabelHostGate({ children }) {
  const host = typeof window !== "undefined"
    ? (window.location.hostname || "").toLowerCase()
    : "";
  const location = useLocation();
  const forceSlug = resolveWhitelabel(host);
  if (forceSlug) {
    // iter377 — the POS staff terminal (/staff/*) must reach the platform
    // <Routes> even on a whitelabel host, so a directory restaurant's own
    // POS auto-populates (RaroBrandProvider resolves the vendor from the
    // host). iter-admin — likewise /admin/* reaches the shared operator
    // console ({domain}/admin parity, business-scoped by the owner's login).
    // Everything else still short-circuits into the vendor site.
    const path = location.pathname || "";
    if (/^\/(staff|raro\/staff|admin)(\/|$)/i.test(path)) {
      return children;
    }
    return (
      <Routes>
        <Route path="/*" element={<SiteRouter forceSlug={forceSlug} />} />
      </Routes>
    );
  }
  return children;
}

/**
 * RaroHostGate (iter376 — 2026-02-25)
 * ────────────────────────────────────
 * Mirrors WhitelabelHostGate for raro.au + mirrors.  Before this, the
 * top-level <Route path="/menu"…> at App.js:767 was intercepting Raro's
 * /menu and rendering the platform's generic MenuPage (HustleSuite
 * chrome, "Join Rewards" strip, "Search, customise" copy) — the
 * exact page the user asked to be deleted 3+ sessions running.
 *
 * On any raro-site host we now short-circuit EVERY path — root AND
 * sub-paths — into <RaroSiteRouter/>, which owns /menu, /book,
 * /takeaway, /table/:n, /pos, /admin, /contact, /area/:slug and /
 * (RaroLandingPage). Non-raro hosts render children unchanged.
 */
function RaroHostGate({ children }) {
  const host = typeof window !== "undefined"
    ? (window.location.hostname || "").toLowerCase()
    : "";
  const path = typeof window !== "undefined"
    ? (window.location.pathname || "").toLowerCase()
    : "";
  // iter-admin: the shared operator back-office (/admin + /admin/*) is
  // business-scoped (get_business_id → the owner's business_id) and reused
  // AS-IS on venue hosts so {domain}/admin opens the real HustlePlate admin
  // console. Let those paths fall through to the main route tree instead of
  // the venue site router (which only has the thin /venue-admin page).
  const isAdminConsole = path === "/admin" || path.startsWith("/admin/");
  if (resolveRaro(host) && !isAdminConsole) {
    return <RaroSiteRouter />;
  }
  return children;
}

/** ChromeGate — decides whether to render the global top bar.
 *
 * Hides the black GoogleTranslateBar (+ Emergency/Modules/Language/
 * Theme pills) on customer scan landings so a QR scan feels like a
 * designed product, not a page nested in a suite:
 *   • path `/f/:publicId`  and `/qr/:publicId`  (QR public routes)
 *   • hostname `qrpal.au` and its `www.` subdomain (customer-facing
 *     scan domain — the entire host is chrome-free for future
 *     white-label parity with `card.<client>.com.au`)
 *   • `?embed=1` query (WP plugin iframe embeds)
 */
function ChromeGate() {
  const location = useLocation();
  const path = location.pathname || "";
  const isEmbed = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("embed") === "1";
  // iter373c patch J (2026-09-23) — whitelabel hosts (taxassistoxley.au,
  // etc.) get their OWN slim top bar (WhitelabelTopBar) with just:
  //   • Left  — language picker
  //   • Right — live clock + light/dark toggle
  // NO HustleSuite modules dropdown, Emergency menu, or Account link.
  const host = typeof window !== "undefined"
    ? (window.location.hostname || "").toLowerCase()
    : "";
  if (resolveWhitelabel(host)) return <WhitelabelTopBar />;
  // iter375 R3 (2026-09-24) — raro.au + mirrors also get the slim
  // WhitelabelTopBar (language + clock + light/dark), no HustleSuite
  // Modules/Emergency/Account chrome.  Public-facing standalone
  // restaurant site.
  if (resolveRaro(host)) return <WhitelabelTopBar />;
  // iter375 R4 (2026-02) — same treatment when the visitor is on the
  // pod / preview URL under /raro/* — never leak HustleSuite chrome
  // over the demo restaurant.
  if (path === "/raro" || path.startsWith("/raro/")) return <WhitelabelTopBar />;
  // Hide the top language/modules bar ONLY when the visitor is
  // looking at a scanned QR card itself (/f/:publicId + /qr/:publicId),
  // or an embedded WP iframe. Everywhere else — including the
  // qrpal.au apex placeholder and the hustleqr.au sales page —
  // renders the full global header so users still get the Suite's
  // Other-Modules dropdown + language selector. Scan cards get their
  // own compact language picker (see `<CardLanguagePicker />` in
  // QRPublicPage).
  const isScanPath = path.startsWith("/f/") || path.startsWith("/qr/");
  if (isScanPath || isEmbed) return null;
  return <GoogleTranslateBar />;
}

function RequireAdmin({ children }) {
  const { user, checking } = useAuth();
  if (checking) {
    return (
      <div
        data-testid="auth-loading"
        className="min-h-screen flex items-center justify-center text-[#5C625E]"
      >
        Loading…
      </div>
    );
  }
  const ADMIN_ROLES = new Set(["admin", "agency_owner", "business_admin"]);
  if (!user || !ADMIN_ROLES.has(user.role)) return <Navigate to="/admin/login" replace />;
  return children;
}

function RequireAgencyOwner({ children }) {
  const { user, checking } = useAuth();
  if (checking) {
    return (
      <div
        data-testid="auth-loading"
        className="min-h-screen flex items-center justify-center text-[#5C625E]"
      >
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  // Vendors get bounced to their own admin — they cannot see /agency.
  if (user.role !== "agency_owner" && user.role !== "admin") {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

export default function App() {
  // iter325b — inject per-host canonical + JSON-LD (Organization +
  // WebSite + FAQPage) via /api/seo/meta. Runs once at the top of the
  // React tree so every route carries the correct SEO/AEO head tags.
  useSEO();

  // Strip the Emergent preview badge globally — it re-injects asynchronously
  // so we kill it on a brief poll after mount. (Doesn't affect deployed apps.)
  // ALSO strip Google Translate's injected top banner (which shows a cream
  // strip above our black GlobalSuiteBar and offsets body by 40 px) — CSS
  // already targets it, but Google re-inserts inline `body.style.top` so we
  // enforce it in JS too.
  useEffect(() => {
    let n = 0;
    const t = setInterval(() => {
      const b = document.getElementById("emergent-badge");
      if (b) b.remove();
      document.querySelectorAll(
        ".goog-te-banner-frame, iframe.skiptranslate.goog-te-banner-frame"
      ).forEach((el) => el.remove());
      if (document.body.style.top && document.body.style.top !== "0px") {
        document.body.style.top = "0px";
      }
      if (++n > 20) clearInterval(t);
    }, 250);
    // Long-lived observer — every DOM mutation (Google Translate flips
    // language during navigation and re-injects the banner), remove it
    // and reset body.top. Cheap: only runs on childList changes.
    const obs = new MutationObserver(() => {
      document.querySelectorAll(
        ".goog-te-banner-frame, iframe.skiptranslate.goog-te-banner-frame"
      ).forEach((el) => el.remove());
      if (document.body.style.top && document.body.style.top !== "0px") {
        document.body.style.top = "0px";
      }
    });
    obs.observe(document.body, { childList: true, subtree: false });
    return () => { clearInterval(t); obs.disconnect(); };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BusinessProvider>
        <ThemeInjector />
        <SettingsProvider>
          <DietaryProvider>
          <DietaryProfileProvider>
          <CartProvider>
          <VoiceActionsProvider>
          <BrowserRouter basename={typeof window !== "undefined" && window.location.pathname.startsWith("/api/uploads/ssp") ? "/api/uploads/ssp" : ""}>
            <DemoModeBanner />
            <GlobalDeepLinkHandler />
            <CrossModuleVoiceActions />
            <AccessibilityWidget />
            <PlazaHostLockdown>
            <AppHostGate>
            <ModuleBranding />
            {/* iter 247: GlobalSuiteBar was the OLD "SUITE | Home | Eats
             *  | Plaza | ..." row above the black GoogleTranslateBar. It
             *  duplicates ModulesMenu (now inside GoogleTranslateBar) and
             *  its reserved 32-px space was leaking as a visible gap
             *  above the top bar. Kill it entirely — GoogleTranslateBar
             *  is the single top-of-screen chrome now.
             *
             *  iter247-e: Restored GTB on hustlefind.au. The user asked
             *  for consistency across the suite — every domain shows the
             *  same tight 37 px black utility bar (Emergency + Modules +
             *  Account + Language + Theme). No more phone-app carve-out.
             *
             *  iter276: QR-scan landings (/f/:publicId + /qr/:publicId)
             *  and the whole qrpal.au host are chrome-free — the card
             *  is the product, the black translate bar breaks the
             *  "designed thing that arrived from a scan" vibe. Same
             *  rule applies to `?embed=1` iframes (WP plugin shell). */}
            <ChromeGate />
            <ConsoleReturnPill />
            <Suspense fallback={null}>
            <WhitelabelHostGate>
            <RaroHostGate>
            <Routes>
              <Route
                path="/"
                element={
                  typeof window !== "undefined" && (() => {
                    const h = window.location.hostname
                      .replace(/^www\./, "");
                    if (h.startsWith("fleet.hustlesuite."))
                      return <FleetStatusPage />;
                    if (h.startsWith("status.hustlesuite."))
                      return <FleetStatusPage publicView />;
                    if (h.startsWith("hustlefind."))
                      return <HustleFinderPage />;
                    if (h.startsWith("hustletickets."))
                      return <HustleTicketsLandingPage />;
                    if (h.startsWith("hustlecircles."))
                      return <HustleMeetCirclesSalesPage />;
                    if (h.startsWith("hustlematchmaker."))
                      return <HustleMeetMatchmakerSalesPage />;
                    // HustleQR — bare-domain sales page on hustleqr.au (+www).
                    // Subdomains (app./demo./reviews.) fall through to
                    // RootRouter which routes to the customer QR admin.
                    if (h === "hustleqr.au")
                      return <HustleQRSalesPage />;
                    // HustleVideos — four domains all hit the same
                    // marketing landing (.com.au / .au / .shop / .ai).
                    // The page reads window.location.hostname to swap
                    // its TLD badge.
                    if (h === "hustlevideos.au" || h === "hustlevideos.com.au" ||
                        h === "hustlevideos.shop" || h === "hustlevideos.ai")
                      return <HustleVideosSalesPage />;
                    // qrpal.au — the scan destination. If somebody hits the
                    // apex without a /f/<publicId>, show a friendly placeholder
                    // pushing them to hustleqr.au for the marketing story.
                    if (h === "qrpal.au")
                      return <QRPalPlaceholderPage />;
                    // admin.qrpal.au (iter372) — agency super-admin dashboard.
                    // The component itself calls /auth/me and either shows the
                    // dashboard (role admin/agency_owner/business_admin) or
                    // redirects to /login. Non-admin authenticated users fall
                    // through to the normal QR panel via RootRouter.
                    if (h === "admin.qrpal.au")
                      return <AgencyAdminDashboard />;
                    // SeatingMaker spin-off (Sprint 3.1, Feb 2026):
                    //  - app.hustleseatmaps.au / app.seatmaps.au → editor
                    //  - hustleseatmaps.au / seatmaps.au (root) → sales
                    if (h.startsWith("app.hustleseatmaps.") ||
                        h.startsWith("app.seatmaps."))
                      return <SeatingMakerEditorPage />;
                    if (h.endsWith("hustleseatmaps.au") ||
                        h.endsWith("seatmaps.au"))
                      return <SeatingMakerLandingPage />;
                    return <RootRouter />;
                  })()
                }
              />
              <Route path="/connect" element={<HustleConnectPage />} />
              <Route path="/pwa" element={<PwaLauncherPage />} />
              <Route path="/hustleeats-sales" element={<HustleEatsSalesPage />} />
              <Route path="/hustleconnect-sales" element={<HustleConnectSalesPage />} />
              <Route path="/hustlereviews-sales" element={<HustleReviewsSalesPage />} />
              <Route path="/hustlecrm-sales" element={<HustleCRMSalesPage />} />
              <Route path="/hustlepress-sales" element={<HustlePressSalesPage />} />
              <Route path="/hustlenews" element={<HustleNewsSalesPage />} />
              <Route path="/hustlenews-lp" element={<HustleNewsSalesPage />} />
              <Route path="/hustlecrm" element={<HustleCRMSalesPage />} />
              <Route path="/hustlepress-lp" element={<HustlePressSalesPage />} />
              <Route path="/deals" element={<HustleDealsPage />} />
              <Route path="/campus" element={<CampusPage />} />
              <Route element={<SiteLayout />}>
                <Route path="/menu" element={<MenuPage />} />
                <Route path="/menu/quick" element={<QuickMenuPage />} />
                <Route path="/menu/specials" element={<ChefSpecialsPrintPage />} />
                <Route path="/book" element={<BookingPage />} />
                <Route path="/order" element={<CoffeeOrderPage />} />
                <Route path="/reviews" element={<ReviewsPage />} />
                <Route path="/reviews/respond/:token" element={<ReviewRespondPage />} />
                <Route path="/rewards/snap" element={<SnapEarnPage />} />
                <Route path="/property/calculator" element={<FeesCalculatorPage variant="property" />} />
                <Route path="/diy/calculator" element={<FeesCalculatorPage variant="diy" />} />
                <Route path="/login/handoff" element={<LoginHandoffPage />} />
                <Route path="/recipes" element={<RecipesIndexRouter />} />
                <Route path="/recipes/:slug" element={<RecipesDetailRouter />} />
                <Route path="/account" element={<AccountPage />} />
                <Route path="/about" element={<CMSPage slug="about" />} />
                <Route path="/p/:slug" element={<CMSPage />} />
                <Route path="/locations/*" element={<LocationPageWrapper />} />
                <Route path="/blog" element={<BlogIndexPage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/discover" element={<DiscoverPage />} />
              </Route>
              <Route path="/privacy" element={<PrivacyRoute />} />
              <Route path="/terms" element={<TermsRoute />} />
              <Route path="/legal/privacy" element={
                <LegalPageShell title="Privacy Policy">
                  <HustleSuitePrivacyPage />
                </LegalPageShell>} />
              <Route path="/legal/terms" element={
                <LegalPageShell title="Terms of Use">
                  <HustleSuiteTermsPage />
                </LegalPageShell>} />
              <Route path="/legal/disclaimer" element={
                <LegalPageShell title="Disclaimer">
                  <HustleSuiteDisclaimerPage />
                </LegalPageShell>} />
              <Route path="/disclaimer" element={
                <LegalPageShell title="Disclaimer">
                  <HustleSuiteDisclaimerPage />
                </LegalPageShell>} />
              <Route path="/support" element={
                <LegalPageShell title="Support">
                  <SupportPage />
                </LegalPageShell>} />
              <Route path="/mic-test" element={<MicTestPage />} />
              <Route path="/cloudflare-setup/:siteKey" element={<CloudflareSetupWizard />} />
              <Route path="/credits" element={<CreditsPage />} />
              <Route path="/finder" element={<HustleFinderPage />} />
              {/* HustleEvents — ticketing platform (app.hustleevents.au) */}
              <Route path="/hustle-events" element={<HustleEventsCatalogPage />} />
              <Route path="/hustle-events/success" element={<HustleEventSuccessPage />} />
              <Route path="/hustle-events/:slug" element={<HustleEventDetailPage />} />
              <Route path="/admin/hustle-events" element={<AdminHustleEventsPage />} />
              {/* Marketplace Ledger — buyer & vendor "money moves" screens */}
              <Route path="/account/ledger" element={<MyLedgerPage />} />
              <Route path="/vendor/ledger" element={<VendorLedgerPage />} />
              <Route path="/vendor/settings" element={<VendorSettingsPage />} />
              <Route path="/vendor/:vendor_id/ledger" element={<VendorLedgerPage />} />
              {/* Voucher & Gift flows */}
              <Route path="/vouchers" element={<VoucherStorefrontPage />} />
              <Route path="/v/:vendorSlug" element={<VendorPublicPage />} />
              <Route path="/meet" element={<HustleMeetLandingPage />} />
              <Route path="/meet/id-check" element={<HustleMeetIDCheckPage />} />
              <Route path="/venue-terminal/:vendorId" element={<VenueTerminalPage />} />
              <Route path="/venue-terminal/:vendorId/kitchen" element={<KitchenDisplayPage />} />
              <Route path="/venue-terminal/:vendorId/login" element={<VenuePinLoginPage />} />
              <Route path="/venue-partners/apply" element={<VenuePartnerApplyPage />} />
              <Route path="/venue-partners/dashboard/:vendorId" element={<VenuePartnerDashboardPage />} />
              <Route path="/circles" element={<HustleMeetCirclesSalesPage />} />
              <Route path="/matchmaker" element={<HustleMeetMatchmakerSalesPage />} />
              <Route path="/matchmaker/intake" element={<HustleMeetMatchmakerIntakePage />} />
              <Route path="/hustlemeetups" element={<HustleMeetupsOverviewPage />} />
              <Route path="/verified-members" element={<HustleMeetupsVerifiedMembersPage />} />
              <Route path="/verified-members/" element={<HustleMeetupsVerifiedMembersPage />} />
              <Route path="/hustleroundup" element={<HustleRoundupSalesPage />} />
              <Route path="/circles/:slugOrId" element={<HustleMeetCircleDetailPage />} />
              <Route path="/meet/profile" element={<HustleMeetProfilePage />} />
              <Route path="/meet/host/apply" element={<HustleMeetHostApplyPage />} />
              <Route path="/meet/host/mine" element={<HustleMeetHostDashboardPage />} />
              <Route path="/meet/events/:eventId/wrap-up" element={<HustleMeetPostEventPage />} />
              <Route path="/admin/hustlemeet-settings" element={<AdminHustleMeetSettingsPage />} />
              <Route path="/admin/hustlemeet-matchmaker" element={<AdminHustleMeetMatchmakerPage />} />
              <Route path="/admin/hustlemeet-host-apps" element={<AdminHustleMeetHostAppsPage />} />
              <Route path="/gifts/:code" element={<GiftClaimPage />} />
              <Route path="/admin/vouchers" element={<AdminVoucherTemplatesPage />} />
              <Route path="/roadtrip" element={<RoadtripPage />} />
              <Route path="/roadtrip/drive" element={<RoadtripDrivePage />} />
              {/* HustleMoments — Memogram-equivalent UGC add-on */}
              <Route path="/moments" element={<MomentsLandingPage />} />
              <Route path="/moments/dashboard" element={<MomentsDashboardPage />} />
              <Route path="/moments/pages/:pageId" element={<MomentsPageDetailPage />} />
              <Route path="/m/:slug" element={<MomentPublicUploadPage />} />
              <Route path="/m/:slug/live" element={<MomentSlideshowPage />} />
              <Route path="/room" element={<HustleMeetLobbyPage />} />
              <Route path="/room/:roomName" element={<HustleMeetRoomPage />} />
              <Route path="/meet/rate/:eventId" element={<HustleMeetRatePage />} />
              <Route path="/points" element={<HustlePointsWalletPage />} />
              <Route path="/points/giftpack/success" element={<HustlePointsWalletPage />} />
              <Route path="/legal/hustlepoints-terms" element={<HustlePointsTermsPage />} />
              <Route path="/agency/points-ops" element={<PointsOpsPage />} />
              <Route path="/vendor/bella-cucina/checkout" element={<BellaCucinaCheckoutPage />} />
              <Route path="/legal/gst" element={<GstPage />} />
              <Route path="/hotels/:slug" element={<HotelDetailPage />} />
              <Route path="/finder/poi/:id" element={<FinderPoiDetailPage />} />
              <Route path="/finder/puzzles" element={<FinderPuzzlesPage />} />
              <Route path="/finder/puzzles/sudoku" element={<FinderSudokuPage />} />
              <Route path="/finder/puzzles/crossword" element={<FinderCrosswordPage />} />
              <Route path="/finder/kids" element={<FinderKidZonePage />} />
              <Route path="/finder/kids/mazes" element={<FinderMazesPage />} />
              <Route path="/finder/kids/colouring" element={<FinderColouringPage />} />
              <Route path="/finder/kids/jigsaw" element={<FinderJigsawPage />} />
              <Route path="/admin/login" element={<LoginPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/magic" element={<MagicLinkVerifyPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/docs/:slug" element={<DocsPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/signup" element={<RegisterPage />} />
              <Route
                path="/agency"
                element={
                  <RequireAgencyOwner>
                    <AgencyControlPanel />
                  </RequireAgencyOwner>
                }
              />
              <Route
                path="/agency/settings"
                element={
                  <RequireAgencyOwner>
                    <AgencySettingsPage />
                  </RequireAgencyOwner>
                }
              />
              <Route
                path="/agency/status"
                element={
                  <RequireAgencyOwner>
                    <AgencyFleetStatusPage />
                  </RequireAgencyOwner>
                }
              />
              <Route
                path="/venue/console"
                element={
                  <RequireAgencyOwner>
                    <VenueConsolePage />
                  </RequireAgencyOwner>
                }
              />
              <Route
                path="/agency/console"
                element={
                  <RequireAgencyOwner>
                    <AgencyConsolePage />
                  </RequireAgencyOwner>
                }
              />
              <Route
                path="/agency/console/vertical-groups"
                element={
                  <RequireAgencyOwner>
                    <VerticalGroupsEditorPage />
                  </RequireAgencyOwner>
                }
              />
              <Route
                path="/agency/uploads/api-keys"
                element={
                  <RequireAgencyOwner>
                    <AgencyApiKeysPage />
                  </RequireAgencyOwner>
                }
              />
              <Route
                path="/portal"
                element={
                  <RequireAdmin>
                    <BusinessPortalPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/portal/workflows"
                element={
                  <RequireAdmin>
                    <WorkflowsPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/portal/agency-engine"
                element={
                  <RequireAdmin>
                    <AgencyEnginePage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/portal/support"
                element={
                  <RequireAdmin>
                    <BusinessPortalSupportPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/portal/api-keys"
                element={
                  <RequireAdmin>
                    <PortalApiKeysPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/portal/subscription"
                element={
                  <RequireAdmin>
                    <BusinessPortalSubscriptionPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/agency/import"
                element={
                  <RequireAgencyOwner>
                    <AgencyBulkImportPage />
                  </RequireAgencyOwner>
                }
              />
              <Route
                path="/agency/claims"
                element={
                  <RequireAgencyOwner>
                    <ClaimDashboardPage />
                  </RequireAgencyOwner>
                }
              />
              <Route
                path="/agency/loyalty-pool"
                element={
                  <RequireAgencyOwner>
                    <AdminLoyaltyPoolPage />
                  </RequireAgencyOwner>
                }
              />
              <Route
                path="/agency/hc-settings"
                element={
                  <RequireAgencyOwner>
                    <HCSettingsPage />
                  </RequireAgencyOwner>
                }
              />
              <Route path="/order/success" element={<OrderSuccessPage />} />
              <Route path="/order/cancel" element={<OrderCancelPage />} />
              <Route path="/orders/:orderId/decide" element={<DszOrderDecidePage />} />
              <Route path="/group-dining/new" element={<GroupDiningCreatePage />} />
              <Route path="/group-dining/:gid" element={<GroupDiningPage />} />
              <Route path="/group-dining/:gid/menu" element={<GroupMenuPickerPage />} />
              <Route path="/group-dining/:gid/invite" element={<GroupDiningPage />} />
              <Route path="/pickup/orders/:orderId" element={<PickupOrderPage />} />
              <Route path="/vendor/scanner/:standId" element={<VendorScannerPage />} />
              <Route path="/live/brisbane" element={<BrisbaneLivePulsePage />} />
              <Route path="/wp-backup/scan" element={<BackupDemoScanPage />} />
              <Route path="/recipe-planner" element={<RecipePlannerPage />} />
              <Route path="/stays" element={<HustleStaysPage />} />
              <Route path="/stays/host" element={<StaysHostDashboardPage />} />
              <Route path="/stays/list-property" element={<StaysListPropertyPage />} />
              <Route path="/stays/find-me" element={<StaysFindMePage />} />
              <Route path="/stay/book" element={<StayBookPage />} />
              <Route path="/stays/p/:propertyId/compliance" element={<StaysComplianceWizardPage />} />
              <Route path="/scale" element={<HustleScaleSalesPage />} />
              <Route path="/scale/:vertical" element={<HustleScaleSalesPage />} />
              <Route path="/stays/p/:propertyId/welcome-book" element={<StaysWelcomeBookPage />} />
              <Route path="/stays/w/:propertyId" element={<StaysWelcomeBookPublicPage />} />
              <Route path="/my-cookbook" element={<MyCookbookPage />} />
              <Route path="/cookbook/:slug" element={<PlannerRecipeDetailPage />} />
              <Route path="/admin/michelin" element={<AdminMichelinPage />} />
              <Route path="/admin/marketing-studio" element={<AdminMarketingStudioPage />} />
              <Route path="/admin/agents" element={<AgentTeamMindMap />} />
              <Route path="/admin/agents/mindmap" element={<AgentTeamMindMap />} />
              <Route path="/admin/agents/dashboard" element={<AgentTeamDashboard />} />
              <Route path="/admin/sites/:slug/search" element={<SearchFeaturesEditor />} />
              <Route path="/admin/multilingual-check" element={<MultilingualCheckPage />} />
              <Route path="/audit/multilingual" element={<MultilingualAuditLandingPage />} />
              <Route path="/admin/site-cloner" element={<SiteClonerPage />} />
              <Route path="/admin/site-cloner/snapshots" element={<SnapshotsTimelinePage />} />
              <Route path="/admin/leads/multilingual" element={<LeadsMultilingualPage />} />
              <Route path="/admin/custom-domains" element={<CustomDomainsPage />} />
              <Route path="/onboarding/website" element={<OnboardingWebsiteWizard />} />
              <Route path="/onboarding/polish/:slug" element={<AfterClonePolishWizard />} />
              <Route path="/platform" element={<SalesPage />} />
              <Route path="/hustlesuite-assistant" element={<HustleSuiteAssistantPage />} />
              <Route path="/membership/community/tourism" element={<MembershipTourismPage />} />
              <Route path="/membership" element={<HustleSuiteSalesPage />} />
              <Route path="/suite-polish" element={<SuitePolishPage />} />
              <Route path="/sales" element={<SalesPage />} />
              <Route path="/directory" element={<DirectoryPage />} />
              <Route path="/olympic" element={<OlympicQuickTour />} />
              <Route path="/admin/practice/:slug" element={<PracticeAdminPanel />} />
              <Route path="/admin/claim-onboard" element={<ClaimOnboardPage />} />
              <Route path="/directory/au" element={<DirectoryAUPage />} />
              <Route path="/coffee-commune" element={<CoffeeCommunePage />} />
              <Route path="/pitch/owner" element={<OwnerPitchPage />} />
              <Route path="/pitch/diner" element={<DinerPitchPage />} />
              <Route path="/pitch/investor" element={<InvestorPitchPage />} />
              <Route path="/pitch/salon" element={<SalonPitchPage />} />
              <Route path="/plaza" element={<HustlePlazaPage />} />
              <Route path="/plaza/shop" element={<HustlePlazaShopPage />} />
              <Route path="/shop" element={<HustlePlazaShopPage />} />

              {/* ── Polish Foodies tenant (migrated from polishfoodies.com) ── */}
              <Route path="/sites/polish-foodies" element={<PolishFoodiesLayout />}>
                <Route index element={<PolishFoodiesHome />} />
                <Route path="club" element={<PolishFoodiesClub />} />
                <Route path="recipes" element={<PolishFoodiesRecipes />} />
                <Route path="recipes/:slug" element={<PolishFoodiesRecipeDetail />} />
                <Route path="shop" element={<PolishFoodiesShop />} />
                <Route path="shop/:slug" element={<PolishFoodiesProductDetail />} />
                <Route path="checkout" element={<PolishFoodiesCheckout />} />
                <Route path="members" element={<PolishFoodiesMembers />} />
                <Route path="about" element={<PolishFoodiesAbout />} />
                <Route path="contact" element={<PolishFoodiesContact />} />
                <Route path="admin/settings" element={<PolishFoodiesAdmin />} />
                <Route path="fulfilment" element={<PolishFoodiesFulfilment />} />
              </Route>

              <Route path="/tackle" element={<TackleShopPage />} />
              <Route path="/tackle.au" element={<TackleShopPage />} />
              <Route path="/admin/reimbursements" element={<AdminReimbursementsPage />} />
              <Route path="/pos/redeem" element={<PosRedeemPage />} />
              <Route path="/restaurant-agreement" element={<RestaurantAgreementPage />} />
              <Route path="/venue-signup/:token" element={<VenueSignupPage />} />
              <Route path="/fishing-tours" element={<FishingToursPage />} />
              <Route path="/fishing-tours/:slug" element={<FishingTourDetailPage />} />
              <Route path="/fish-map" element={<FishMapPage />} />
              <Route path="/fish-map/hall-of-fame" element={<FishMapHallOfFamePage />} />
              <Route path="/fish-finder" element={<FishMapPage />} />
              <Route path="/up" element={<HustleUpDiscoverPage />} />
              <Route path="/up/groups" element={<HustleUpDiscoverPage />} />
              <Route path="/up/g/:slug" element={<HustleUpPublicGroupPage />} />
              <Route path="/up/e/:event_id" element={<HustleUpPublicEventPage />} />
              <Route path="/account/settings" element={<AccountSettingsPage />} />
              <Route path="/account" element={<AccountSettingsPage />} />
              <Route path="/account/voice" element={<HustleVoiceSettingsPage />} />
              <Route path="/voice/settings" element={<HustleVoiceSettingsPage />} />
              <Route path="/admin/voice/wake-phrases" element={<AdminWakePhrasesPage />} />
              <Route path="/admin/video-studio" element={<AdminVideoStudioPage />} />
              <Route path="/admin/video-studio/create" element={<AdminCreateVideoPage />} />
              <Route path="/admin/quick-add" element={<AdminQuickAddPage />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/verify/photo/:token" element={<VerifyPhotoTokenPage />} />
              <Route path="/showcase" element={<ShowcasePage />} />
              <Route path="/showcase/success" element={<ShowcaseSuccessPage />} />
              <Route path="/hydro" element={<HydroReportPage />} />
              <Route path="/hydro/compare" element={<HydroComparePage />} />
              <Route path="/hydro/api" element={<HydroApiPage />} />
              <Route path="/hustlepress" element={<HustlePressPage />} />
              <Route path="/error" element={<FriendlyErrorPage code={500} />} />
              <Route path="/hustlepress/start" element={<HustlePressStartPage />} />
              <Route path="/hustlepress/success" element={<HustlePressSuccessPage />} />
              <Route path="/account/api-keys" element={<ApiKeysPage />} />
              <Route path="/account/saved-properties" element={<SavedPropertiesPage />} />
              <Route path="/account/credits" element={<AccountCreditsPage />} />
              <Route path="/hustle-emergency" element={<HustleEmergencyPage />} />
              <Route path="/emergency" element={<HustleEmergencyPage />} />
              <Route path="/hustleshop" element={<HustleShopPage />} />
              <Route path="/store/hustleshop" element={<HustleShopPage />} />
              <Route path="/store/amazon" element={<HustleShopPage />} />
              <Route path="/property/seller-pack" element={<SellerPackPage />} />
              <Route path="/seller-pack" element={<SellerPackPage />} />
              <Route path="/admin/trust-accounts" element={<TrustAdminPage />} />
              <Route path="/trust" element={<TrustAdminPage />} />
              <Route path="/operator-claim/:token" element={<OperatorClaimPage />} />
              <Route path="/hustleup" element={<HustleUpLandingPage />} />
              <Route path="/hustleup/join" element={<HustleUpJoinPage />} />
              <Route path="/hustleup/me" element={<HustleUpMePage />} />
              <Route path="/hustleup/organisers" element={<HustleUpOrganisersPage />} />
              <Route path="/hustleup/switch" element={<HustleUpOrganisersPage />} />
              <Route path="/hustleup/groups" element={<HustleUpGroupsPage />} />
              <Route path="/hustleup/groups/new" element={<HustleUpGroupCreatePage />} />
              <Route path="/hustleup/groups/:slug" element={<HustleUpGroupDetailPage />} />
              <Route path="/hustleup/groups/:slug/events/new" element={<HustleUpEventCreatePage />} />
              <Route path="/hustleup/event/:eventId" element={<HustleUpEventPage />} />
              <Route path="/hustleup/messages" element={<HustleUpMessagesPage />} />
              <Route path="/hustlechat" element={<HustleChatInboxPage />} />
              <Route path="/hustlechat/new" element={<HustleChatNewPage />} />
              <Route path="/hustlechat/c/:convId" element={<HustleChatThreadPage />} />
              <Route path="/admin/hustlechat/reports" element={
                <RequireAdmin><AdminChatReportsPage /></RequireAdmin>
              } />
              <Route path="/foh" element={<FohTabletPage />} />
              <Route path="/admin/plaza-pricing" element={<PlazaAdminPricingPage />} />
              <Route path="/plaza/demo" element={<DemoGate><PlazaDemoPage /></DemoGate>} />
              <Route path="/plaza/demo-day" element={<DemoGate><PlazaDemoPage /></DemoGate>} />
              <Route path="/demo" element={<DemoGate><PlazaDemoPage /></DemoGate>} />
              <Route path="/plate" element={<DemoGate><HustlePlateSalesPage /></DemoGate>} />
              <Route path="/demo/abn-registry" element={<AbnRegistryDemoPage />} />
              <Route path="/plaza/abn" element={<AbnRegistryDemoPage />} />
              <Route path="/signup-hub" element={<SignupHubPage />} />
              <Route path="/signup/:manifestId" element={<SignupPage />} />
              <Route path="/admin/abn-registry" element={<AbnRegistryAdminPage />} />
              <Route path="/demo/tour" element={<DemoTourPage />} />
              <Route path="/eat" element={<HustleEatsPage />} />
              <Route path="/eat/wallet" element={<HustleEatsWalletPage />} />
              <Route path="/eat/table/:qr_token" element={<TableSessionPage />} />
              <Route path="/admin/hustleeats" element={<HustleEatsAdminPage />} />
              {/* HustleEats — Bookable In-Venue Events (wine tasting, trivia…) */}
              <Route path="/eats/events" element={<EatsEventsCalendarPage />} />
              <Route path="/eats/events/:id" element={<EatsEventDetailPage />} />
              <Route path="/eats/v/:venue_slug" element={<EatsVenuePage />} />
              <Route path="/admin/eats/events" element={<AdminEatsEventsPage />} />
              <Route path="/admin/eats/events/:id/promo" element={<AdminEventPromoPage />} />
              <Route path="/admin/db-health" element={<AdminDbHealthPage />} />
              <Route path="/admin/directory-rollout" element={<AdminDirectoryRolloutPage />} />
              <Route path="/admin/alerts" element={<AdminAlertsPage />} />
              <Route path="/admin/vpn/blocks" element={<AdminVpnBlocksPage />} />
              <Route path="/admin/property/oft-application" element={<AdminPropertyOftPage />} />
              <Route path="/admin/kitchen/overheads" element={<AdminKitchenOverheadsPage />} />
              <Route path="/admin/kitchen/usage" element={<AdminKitchenUsagePage />} />
              <Route path="/admin/kitchen/reorder" element={<AdminKitchenReorderPage />} />
              <Route path="/account/newsletter" element={<AccountNewsletterPage />} />
              <Route path="/account/hustleconnect" element={<AccountHustleConnectPage />} />
              <Route path="/account/qr-codes" element={<AccountQRCodesPage />} />
              <Route path="/account/vendor-settings" element={<AccountVendorSettingsPage />} />
              <Route path="/account/wallet-certs" element={<AccountWalletCertsPage />} />
              <Route path="/admin/directory"  element={<AdminDirectoryPage />} />
              <Route path="/admin/template-picker"                     element={<AdminTemplatePickerPage />} />
              <Route path="/admin/vendors/:businessId/template-picker" element={<AdminTemplatePickerPage />} />
              <Route path="/qr/:publicId" element={<QRPublicPage />} />
              <Route path="/f/:publicId"  element={<QRPublicPage />} />
              {/* HustleShield Forms — standalone public page (no suite chrome) */}
              <Route path="/form/:slug"   element={<PublicFormView />} />
              {/* Owner-side builder (admins only) */}
              <Route path="/admin/forms" element={
                <RequireAdmin><FormsList /></RequireAdmin>
              } />
              <Route path="/admin/forms/dashboard" element={
                <RequireAdmin><FormsDashboard /></RequireAdmin>
              } />
              <Route path="/admin/forms/:id/edit" element={
                <RequireAdmin><FormBuilder /></RequireAdmin>
              } />
              <Route path="/admin/forms/:id/submissions" element={
                <RequireAdmin><FormSubmissions /></RequireAdmin>
              } />
              <Route path="/admin/forms/shield" element={
                <RequireAdmin><ShieldDashboard /></RequireAdmin>
              } />
              <Route path="/admin/forms/agency" element={
                <RequireAdmin><AgencyConsole /></RequireAdmin>
              } />
              {/* HustleShield public sales pages — nginx will later map
                  hustleshield.au → /hustleshield.au  and
                  emailspamshield.com → /emailspamshield.com */}
              <Route path="/hustleshield.au"       element={<HustleShieldSalesAUD />} />
              <Route path="/hustleshield-au"       element={<HustleShieldSalesAUD />} />
              <Route path="/emailspamshield.com"   element={<HustleShieldSalesUSD />} />
              <Route path="/emailspamshield-com"   element={<HustleShieldSalesUSD />} />
              <Route path="/hustleshield"          element={<HustleShieldSalesAUD />} />
              <Route path="/emailspamshield"       element={<HustleShieldSalesUSD />} />
              <Route path="/t/:tag_id"    element={<TapRouterPageLazy />} />
              <Route path="/raro"                element={<RaroLandingPage />} />
              <Route path="/raro/menu"           element={<RaroMenuPage />} />
              {/* /raro/book intentionally removed — will be rebuilt. */}
              <Route path="/raro/book"           element={<RaroBookingPageLazy />} />
              <Route path="/raro/takeaway"       element={<RaroTakeawayPage />} />
              <Route path="/raro/table/:tableNo" element={<RaroTablePage />} />
              <Route path="/raro/pos"            element={<RaroPosPage />} />
              <Route path="/raro/admin"          element={<RaroAdminPage />} />
              <Route path="/raro/loyalty"        element={<RaroLandingPage />} />
              <Route path="/raro/contact"        element={<RaroLandingPage />} />
              {/* Staff Terminal (PIN-gated, RaroBrandProvider-wrapped for token pull) */}
              <Route path="/raro/staff/login"    element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffLoginPage /></RaroBrandProvider>} />
              <Route path="/raro/staff"          element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffTerminalPage /></RaroBrandProvider>} />
              <Route path="/raro/staff/kitchen"  element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffKitchenPage /></RaroBrandProvider>} />
              <Route path="/raro/staff/settings" element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffSettingsPage /></RaroBrandProvider>} />
              <Route path="/raro/staff/audit"    element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffAuditPage /></RaroBrandProvider>} />
              <Route path="/raro/staff/qr"       element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffQRPage /></RaroBrandProvider>} />
              <Route path="/raro/staff/close"    element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffClosePage /></RaroBrandProvider>} />
              {/* iter376 · non-prefixed staff terminal routes so VUMU (and any
                  future whitelabel that uses the Raro POS template) can hit
                  clean paths like /staff/login and /pos on their own domain.
                  RaroBrandProvider resolves the vendor from hostname. */}
              <Route path="/pos"                 element={<RaroBrandProvider><RaroOwnerBar /><RaroPosPage /></RaroBrandProvider>} />
              <Route path="/staff/login"         element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffLoginPage /></RaroBrandProvider>} />
              <Route path="/staff"               element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffTerminalPage /></RaroBrandProvider>} />
              <Route path="/staff/kitchen"       element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffKitchenPage /></RaroBrandProvider>} />
              <Route path="/staff/settings"      element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffSettingsPage /></RaroBrandProvider>} />
              <Route path="/staff/audit"         element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffAuditPage /></RaroBrandProvider>} />
              <Route path="/staff/qr"            element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffQRPage /></RaroBrandProvider>} />
              <Route path="/staff/close"         element={<RaroBrandProvider><RaroOwnerBar /><RaroStaffClosePage /></RaroBrandProvider>} />
              <Route path="/fleet"  element={<FleetStatusPage />} />
              <Route path="/status" element={<FleetStatusPage publicView />} />
              <Route path="/events/inquire/:venueSlug" element={<EventInquiryPage />} />
              <Route path="/preview/:council/:suburb" element={<SuburbPreviewPage />} />
              <Route path="/admin/conversions" element={<AdminConversionsPage />} />
              <Route path="/account/venue-events" element={<AccountVenueEventsPage />} />
              <Route path="/demo/personas" element={<DemoPersonasPage />} />
              <Route path="/admin/dropshipzone" element={<DropshipzonePage />} />
              <Route path="/plaza/plate" element={<DemoGate><HustlePlateSalesPage /></DemoGate>} />
              <Route path="/hustleplate" element={<DemoGate><HustlePlateSalesPage /></DemoGate>} />
              {/* HustlePlate — Bigmenu sub-sales pages (Recipe Builder, Terminal, etc.)
                * Public marketing pages — NO DemoGate — reachable on both
                * hustleplate.au (bare) and hustlesuite.au/hustleplate/... */}
              <Route path="/hustleplate/recipe-builder"  element={<HustlePlateRecipeBuilderPage  />} />
              <Route path="/hustleplate/terminal"        element={<HustlePlateTerminalPage       />} />
              <Route path="/hustleplate/bookings"        element={<HustlePlateBookingsPage       />} />
              <Route path="/hustleplate/table-builder"   element={<HustlePlateTableBuilderPage   />} />
              <Route path="/hustleplate/kitchen-display" element={<HustlePlateKitchenDisplayPage />} />
              <Route path="/hustleplate/menu-builder"    element={<HustlePlateMenuBuilderPage    />} />
              <Route path="/hustleplate/inventory"       element={<HustlePlateInventoryPage      />} />
              <Route path="/hustleplate/rewards"         element={<HustlePlateRewardsPage        />} />
              <Route path="/hustleplate/ai-receptionist" element={<HustlePlateAiReceptionistPage />} />
              {/* iter313v — HustleAnswer bare-domain routes.
                * • /hustleanswer         → sales page (also rendered on hustleanswer.au)
                * • /hustleanswer/demo    → interactive demo (also on demo.hustleanswer.au)
                * • /hustleanswer/app     → configurator (also on app.hustleanswer.au)
                */}
              <Route path="/hustleanswer"      element={<HustleAnswerSalesPage />} />
              <Route path="/hustleanswer/demo" element={<HustleAnswerDemoPage  />} />
              <Route path="/hustleanswer/app"  element={<HustleAnswerAppPage   />} />
              {/* HustleQR bare-domain sales page — also reachable on
                * hustleqr.au / www.hustleqr.au via the RootRouter host block. */}
              <Route path="/hustleqr"       element={<HustleQRSalesPage />} />
              <Route path="/hustleqr-sales" element={<HustleQRSalesPage />} />
              <Route path="/hustlevideos"        element={<HustleVideosSalesPage />} />
              <Route path="/hustlevideos-sales"  element={<HustleVideosSalesPage />} />
              <Route path="/stadium-ops"       element={<StadiumOpsConsolePage />} />
              <Route path="/stadium-season-lock-success" element={<SeasonLockSuccessPage />} />
              {/* iter259: short-URL aliases requested by ops so hustleplate.au
                * uses /rewards instead of /hustleplate/rewards. Both sets of
                * paths resolve to the same lazy-loaded page component to
                * preserve backward compatibility with existing deep links. */}
              <Route path="/recipe-builder"  element={<HustlePlateRecipeBuilderPage  />} />
              <Route path="/terminal"        element={<HustlePlateTerminalPage       />} />
              <Route path="/bookings"        element={<HustlePlateBookingsPage       />} />
              <Route path="/table-builder"   element={<HustlePlateTableBuilderPage   />} />
              <Route path="/kitchen-display" element={<HustlePlateKitchenDisplayPage />} />
              <Route path="/menu-builder"    element={<HustlePlateMenuBuilderPage    />} />
              <Route path="/inventory"       element={<HustlePlateInventoryPage      />} />
              <Route path="/rewards"         element={<HustlePlateRewardsPage        />} />
              <Route path="/ai-receptionist" element={<HustlePlateAiReceptionistPage />} />
              {/* iter259: short-URL aliases requested by ops so hustleplate.au
                * uses /rewards instead of /hustleplate/rewards. Both sets of
                * paths resolve to the same lazy-loaded page component to
                * preserve backward compatibility with existing deep links. */}
              <Route path="/recipe-builder"  element={<HustlePlateRecipeBuilderPage  />} />
              <Route path="/terminal"        element={<HustlePlateTerminalPage       />} />
              <Route path="/bookings"        element={<HustlePlateBookingsPage       />} />
              <Route path="/table-builder"   element={<HustlePlateTableBuilderPage   />} />
              <Route path="/kitchen-display" element={<HustlePlateKitchenDisplayPage />} />
              <Route path="/menu-builder"    element={<HustlePlateMenuBuilderPage    />} />
              <Route path="/inventory"       element={<HustlePlateInventoryPage      />} />
              <Route path="/rewards"         element={<HustlePlateRewardsPage        />} />
              <Route path="/ai-receptionist" element={<HustlePlateAiReceptionistPage />} />
              <Route path="/plaza/terms" element={<HustleGroupTermsPage />} />
              <Route path="/hustle-terms" element={<HustleGroupTermsPage />} />
              <Route path="/group-terms" element={<HustleGroupTermsPage />} />
              <Route path="/hustleplaza" element={<HustlePlazaPage />} />
              <Route path="/admin/ai-review/:slug" element={<AICopyReviewPanel />} />
              <Route path="/add-business" element={<AddBusinessPage />} />
              <Route path="/claim-business" element={<AddBusinessPage />} />
              <Route path="/directory-sales" element={<DirectorySalesPage />} />
              <Route path="/hustlestadium" element={<HustleStadiumSalesPage />} />
              <Route path="/stadium-sales" element={<HustleStadiumSalesPage />} />
              <Route path="/admin/taxonomy/queue" element={<TaxonomyQueuePanel />} />
              <Route path="/directory/r/:slug" element={<RestaurantDetailPage />} />
              <Route path="/admin/bookings" element={<AdminBookingsPage />} />
              <Route path="/admin/stays/rate-connect" element={<AdminStayCoverageBadgePage />} />
              <Route path="/admin/stays/hotelbeds" element={<AdminHotelbedsMapPage />} />
              <Route path="/admin/tickets/settings" element={<AdminTicketsSettingsPage />} />
              <Route path="/admin/hustle-events/:eventId/seat-map" element={<AdminHustleEventsSeatMapPage />} />
              <Route path="/admin/hustle-events/:eventId/venue" element={<AdminVenueMakerPage />} />
              <Route path="/admin/marketing/segments" element={<AdminMarketingSegmentsPage />} />
              <Route path="/tickets" element={<TicketsBrowsePage />} />
              <Route path="/tickets/host/new" element={<TicketsPromoterWizardPage />} />
              <Route path="/tickets/e/:eventId" element={<TicketsEventDetailPage />} />
              {/* HustleTickets Australianised sales pages (Feb 2026) —
               *  multi-page TicketSpice-style structure. Accessible both
               *  on hustletickets.au and the main app. */}
              <Route path="/hustletickets" element={<HustleTicketsLandingPage />} />
              <Route path="/hustletickets/features" element={<HustleTicketsFeaturesPage />} />
              <Route path="/hustletickets/pricing" element={<HustleTicketsPricingPage />} />
              <Route path="/hustletickets/solutions" element={<HustleTicketsSolutionsPage />} />
              <Route path="/hustletickets/faq" element={<HustleTicketsFaqPage />} />
              <Route path="/hustletickets/contact" element={<HustleTicketsContactPage />} />
              {/* SeatingMaker spin-off marketing page (Feb 2026) —
               *  standalone pricing/personas surface for the seat-map
               *  editor. Ready to move to `seatingmaker.au` root once
               *  the user signs off on the pricing tiers. */}
              <Route path="/seatingmaker" element={<SeatingMakerLandingPage />} />
              <Route path="/seatingmaker/edit/:mapId" element={<SeatingMakerMapEditPage />} />
              <Route path="/admin/seatingmaker/members" element={<AdminSeatingMakerMembersPage />} />
              <Route path="/hustle-events/:slug/pick" element={<HustleEventsSeatPickerPage />} />
              <Route path="/account/purchases" element={<MyPurchasesPage />} />
              <Route path="/admin/modules" element={<AdminModulesPage />} />
              <Route path="/admin/global-header" element={<AdminGlobalHeaderPage />} />
              <Route path="/admin/inventory" element={<AdminInventoryPage />} />
              <Route path="/admin/ingredients" element={<AdminIngredientsPage />} />
              <Route path="/admin/templates" element={<AdminTemplatesPage />} />
              <Route path="/admin/plaza-settings" element={<PlazaSettingsAdminPage />} />
              <Route path="/admin/newsletter" element={<NewsletterListPage />} />
              <Route path="/admin/newsletter/help" element={<NewsletterHelpPage />} />
              <Route path="/admin/newsletter/:id" element={<NewsletterEditorPage />} />
              <Route path="/admin/newsletter-ops" element={<NewsletterOpsPage />} />
              {/* Public newsletter reader + archive ────────────────── */}
              <Route path="/n/current" element={<PublicNewsletterPage mode="current" />} />
              <Route path="/n/current/:city" element={<PublicNewsletterPage mode="current" />} />
              <Route path="/n/current/:city/:niche" element={<PublicNewsletterPage mode="current" />} />
              <Route path="/n/previous" element={<PublicNewsletterPage mode="previous" />} />
              <Route path="/n/previous/:city" element={<PublicNewsletterPage mode="previous" />} />
              <Route path="/n/previous/:city/:niche" element={<PublicNewsletterPage mode="previous" />} />
              <Route path="/n/more" element={<NewsletterArchivePage />} />
              <Route path="/n/more/:city" element={<NewsletterArchivePage />} />
              <Route path="/n/more/:city/:niche" element={<NewsletterArchivePage />} />
              <Route path="/n/:slug" element={<PublicNewsletterPage mode="by-slug" />} />
              {/* ── HustleProperty (standalone RE platform) ────────── */}
              <Route path="/property" element={<PropertyHomePage />} />
              <Route path="/property/find" element={<MatchMeWizard />} />
              <Route path="/property/match-me" element={<MatchMeWizard />} />
              <Route path="/property/sell/new" element={<SellerListingWizard />} />
              <Route path="/property/sell-info" element={<SellerInfoPage />} />
              <Route path="/eats/floorplan" element={<FloorPlanDesigner />} />
              <Route path="/agency/admin/builder" element={<BuilderStudio />} />
              <Route path="/property/ask" element={<PropertyFindPage />} />
              <Route path="/property/aml-guardian" element={<PropertyAMLPage />} />
              <Route path="/property/aml-vs-syntrico" element={<PropertyAMLPage />} />
              <Route path="/property/aml-for-developers" element={<PropertyAMLForDevelopersPage />} />
              <Route path="/admin/property/aml" element={<AdminAMLDashboard />} />
              <Route path="/admin/property/aml/status" element={<AdminAMLDashboard />} />
              <Route path="/admin/property/aml/integrations" element={<TenantIntegrationsPage />} />
              <Route path="/admin/hc/sites" element={<AdminHcSitesPage />} />
              <Route path="/admin/indexnow-stats" element={<AdminIndexNowStatsPage />} />
              <Route path="/admin/hc/fleet" element={<AdminHcFleetPage />} />
              <Route path="/exposure" element={<AdminExposureGridPage />} />
              <Route element={<MeetupLayout />}>
                <Route path="/meetup" element={<MeetupHomePage />} />
                <Route path="/meetup/onboarding" element={<MeetupOnboardPage />} />
                <Route path="/meetup/g/:slug" element={<MeetupGroupPage />} />
                <Route path="/meetup/e/:id" element={<MeetupEventPage />} />
                <Route path="/meetup/e/:id/welcome-pack" element={<MeetupEventWelcomePackPage />} />
                <Route path="/meetup/buddy-pay/:ticketId" element={<MeetupBuddyPayPage />} />
              </Route>
              <Route path="/hustlechat/room/:roomId" element={<HustleChatRoomPage />} />
              <Route path="/property/trades" element={<PropertyServicesPage />} />
              <Route path="/property/trades/:key" element={<PropertyServiceDetail />} />
              <Route path="/property/suburbs/:postcode" element={<PropertySuburbPage />} />
              <Route path="/property/:intent" element={<PropertyListPage />} />
              <Route path="/property/:intent/listing/:id" element={<PropertyDetailPage />} />
              <Route path="/property/:intent/listing/:id/:slug" element={<PropertyDetailPage />} />
              <Route path="/property/:intent/:city" element={<PropertyListPage />} />
              <Route path="/property/:intent/:city/:suburb" element={<PropertyListPage />} />
              <Route path="/p/:id" element={<PropertyDetailPage />} />
              <Route path="/admin/property" element={<PropertyAdminList />} />
              <Route path="/admin/property/people" element={<PropertyAdminPeople />} />
              <Route path="/admin/property/templates" element={<PropertyTemplatesPage module="property" />} />
              <Route path="/diy/templates" element={<PropertyTemplatesPage module="diy" />} />
              <Route path="/admin/property/geo" element={<PropertyGeoAdminPage />} />
              <Route path="/admin/property/new" element={<PropertyAdminEditor />} />
              <Route path="/admin/property/:id" element={<PropertyAdminEditor />} />
              {/* HustleMusic public application flow */}
              <Route path="/hustlemusic" element={<HustleMusicListenPage />} />
              <Route path="/hustlemusic/apply" element={<HustleMusicApplyPage />} />
              <Route path="/hustlemusic/upload" element={<HustleMusicUploadPage />} />
              <Route path="/admin/hustlemusic" element={<HustleMusicAdminPage />} />
              {/* QuickReserve standalone reservation page (deep-linked from newsletter) */}
              <Route path="/reserve/:venueSlug" element={<ReservePage />} />
              <Route path="/admin/reservations/:venueSlug" element={<RestaurantAdminPage />} />
              <Route path="/admin/agency/diners" element={<AgencyDinersPage />} />
              <Route path="/admin/shortcodes" element={<ShortcodesAdminPage />} />
              <Route path="/newsletter/recipe/:rid" element={<RecipePage />} />
              <Route path="/claim/:token" element={<ClaimPage />} />
              <Route path="/claim-business" element={<ClaimBusinessPage />} />
              <Route path="/claim-business/:slug" element={<ClaimBusinessPage />} />
              <Route path="/r/:slug/*" element={<SiteRouter />} />
              <Route path="/t/:slug/:tableNumber" element={<TableOrderPage />} />
              <Route path="/recipe-gift/:qrToken" element={<RecipeGiftLanding />} />
              {/* Family Groups V1 — household/share hub + invite accept */}
              <Route path="/family" element={<FamilyGroupsPage />} />
              <Route path="/family/invite/:token" element={<FamilyInviteAcceptPage />} />
              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <AdminLayout />
                  </RequireAdmin>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="profile" element={<AdminMyProfilePage />} />
                <Route path="menu" element={<AdminMenu />} />
                <Route path="menu-v3" element={<MenuBuilderV3Page />} />
                <Route path="inventory" element={<AdminInventoryPage />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="bookings" element={<AdminBookings />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="floorplan" element={<AdminFloorplan />} />
                <Route path="availability" element={<AdminAvailability />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="testimonials" element={<AdminTestimonialsPage />} />
                <Route path="pages" element={<AdminPages />} />
                <Route path="menu-import" element={<AdminMenuImport />} />
                <Route path="menu-import/vision" element={<AdminMenuImportVision />} />
                <Route path="venue/geofence" element={<AdminMapGeofence />} />
                <Route path="holidays" element={<AdminHolidays />} />
                <Route path="print" element={<AdminPrintGallery />} />
                <Route path="print/menu" element={<AdminPrintGallery />} />
                <Route path="print/wine" element={<AdminPrintGallery />} />
                <Route path="print/specials" element={<AdminPrintGallery />} />
                <Route path="print/flyer" element={<AdminPrintGallery />} />
                <Route path="print/tents" element={<AdminPrintGallery />} />
                <Route path="vouchers" element={<AdminVouchers />} />
                <Route path="settings/notifications" element={<NotificationPrefsPage />} />
                <Route path="marketing/push" element={<NotificationPrefsPage />} />
                <Route path="marketing/sms" element={<NotificationPrefsPage />} />
                <Route path="delivery" element={<DeliveryProvidersPage />} />
                <Route path="delivery/uber" element={<DeliveryProvidersPage />} />
                <Route path="delivery/doordash" element={<DeliveryProvidersPage />} />
                <Route path="delivery/sherpa" element={<DeliveryProvidersPage />} />
                <Route path="delivery/in-house" element={<DeliveryProvidersPage />} />
                <Route path="integrations/square" element={<SquareIntegrationPage />} />
                <Route path="integrations" element={<IntegrationsHubPage />} />
                <Route path="recipe-gift" element={<RecipeGiftAdminPage />} />
                <Route path="loyalty" element={<AdminLoyaltyPage />} />
                <Route path="gallery" element={<AdminGalleryPage />} />
                <Route path="push" element={<AdminPushPage />} />
                <Route path="media" element={<AdminMediaLibraryPage />} />
                <Route path="payments/square" element={<SquareIntegrationPage />} />
                <Route path="menu-import-smart" element={<SmartMenuImportPage />} />
                <Route path="marketing/qa" element={<MarketingStudioQAPage />} />
                {/* Iter 185-189 — Trust Ops + Rentals + Cyber-Ops (nested under AdminLayout so the sidebar + BankStatusBadge render) */}
                <Route path="property/trust/queue" element={<AdminTrustApprovalQueuePage />} />
                <Route path="property/trust/beneficiaries" element={<AdminTrustBeneficiariesPage />} />
                <Route path="property/trust/audit" element={<AdminTrustAuditPage />} />
                <Route path="property/trust/sweep" element={<AdminTrustSweepDashboardPage />} />
                <Route path="property/rentals" element={<AdminRentalsPage />} />
                <Route path="dsz-oos" element={<AdminDszOosQueuePage />} />
                <Route path="group-dining" element={<AdminGroupDiningPage />} />
                <Route path="notifications" element={<AdminNotificationChannelsPage />} />
                <Route path="exposure-grid" element={<AdminExposureGridPage />} />
                {/* Catch-all for modules in the registry that don't yet have
                    a dedicated page — renders the "Coming soon" placeholder
                    so the dynamic sidebar never produces a 404. */}
                <Route path="*" element={<AdminComingSoon />} />
              </Route>
              <Route path="/resort/:resortSlug" element={<ResortLandingPage />} />
              <Route path="*" element={<WhitelabelWildcard />} />
            </Routes>
            </RaroHostGate>
            </WhitelabelHostGate>
            </Suspense>
            </AppHostGate>
            </PlazaHostLockdown>
          <HustleVoice module="hustle" />
          </BrowserRouter>
          </VoiceActionsProvider>
          <Toaster richColors position="top-center" />
          <LanguageDetectedToast />
          <PwaInstallPrompt />
        </CartProvider>
        </DietaryProfileProvider>
        </DietaryProvider>
        </SettingsProvider>
        </BusinessProvider>
      </AuthProvider>
    </div>
  );
}
